import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../../services/api';

// UI Components
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { isMarketBoosted } from '../../components/shared';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../../components/ui/dropdown-menu';

// Icons
import {
  Clock, TrendingUp, Users, DollarSign, Trophy, AlertCircle, Settings,
  Plus, BarChart3, CheckCircle, XCircle, Calendar, ShoppingCart,
  Gift, Star, Bell, Edit, Trash2, Save, Award,
  Activity, PieChart, LineChart, RefreshCw, Play, Pencil, Heart,
  Target, Zap, CreditCard, MoreVertical,
  LayoutDashboard, FolderTree, Package, Receipt, Sparkles, Gavel,
} from 'lucide-react';

// Sidebar grouping for the admin panel. 14 flat tabs were unwieldy —
// grouping them by domain (Markets / Users / Activity / Monetization)
// makes scanning much faster and frees horizontal space for content.
const ADMIN_NAV_SECTIONS = [
  {
    label: 'Übersicht',
    items: [
      { value: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { value: 'notifications', label: 'Notifications', icon: Bell },
    ],
  },
  {
    label: 'Markets',
    items: [
      { value: 'markets', label: 'Markets', icon: TrendingUp },
      { value: 'approval', label: 'Approval', icon: CheckCircle },
      { value: 'create', label: 'Create', icon: Plus },
      { value: 'settle', label: 'Settle', icon: Gavel },
      { value: 'payouts', label: 'Auszahlungen', icon: Receipt },
      { value: 'categories', label: 'Categories', icon: FolderTree },
    ],
  },
  {
    label: 'Users',
    items: [
      { value: 'users', label: 'Users', icon: Users },
      { value: 'creators', label: 'Creator Applications', icon: Sparkles },
      { value: 'activity-logs', label: 'Activity Logs', icon: Activity },
    ],
  },
  {
    label: 'Activity',
    items: [
      { value: 'bets', label: 'Active Bets', icon: Activity },
      { value: 'transactions', label: 'Transactions', icon: Receipt },
      { value: 'credit-sales', label: 'Credit Sales (€)', icon: DollarSign },
      { value: 'sentry-issues', label: 'Sentry Issues', icon: AlertCircle },
    ],
  },
  {
    label: 'Monetization',
    items: [
      { value: 'virtual-shop', label: 'Virtual Items', icon: Gift },
      { value: 'packages', label: 'Packages', icon: Package },
      { value: 'commissions', label: 'Commissions', icon: DollarSign },
      { value: 'fees', label: 'Fees', icon: Settings },
    ],
  },
];

// Alert components
import { Alert, AlertDescription } from '../../components/ui/alert';

// Toast notifications
import { toast } from 'sonner';

// Custom components
import EditUserDialog from '../../components/admin/EditUserDialog';

// Charts
import {
  LineChart as RechartsLine, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  Area, AreaChart
} from 'recharts';

// Constants
import { CHART_COLORS } from '../../constants';
import { safeExternalHref } from '../../utils/helpers';
import ImageUploadField from '../../components/shared/ImageUploadField';

// Context
import { useAuth } from '../../contexts';
import { useWebSocketContext } from '../../contexts/WebSocketContext';

// Admin Tab Components
import { TransactionsTab } from './tabs';
import SettlementPayoutsCard from './tabs/SettlementPayoutsCard';

// Admin notifications feed — own page under /admin?tab=notifications.
// Row-Klick UND expliziter Check-Button markieren als gelesen; oben
// gibt es einen "Alle als gelesen"-Sammelbutton wenn ungelesene da sind.
const AdminNotificationsCard = () => {
  const { notifications = [], notificationCount = 0, markNotificationAsRead, markAllNotificationsAsRead } = useAuth();
  const { resetNotificationCount } = useWebSocketContext() || {};
  const unreadCount = notifications.filter(n => !n.read).length;
  // Beim Öffnen der Notifications-Seite einmal den Live-Counter auf
  // den aktuellen Stand syncen. Vorher hatten wir hier einen Effect
  // auf [notificationCount] — der hat aber bei jedem WebSocket-Push
  // die neue Notification sofort wieder weggewischt (gleiche
  // Race-Klasse wie der heute im Header gefixte Bug).
  // Mark-Read-Aktionen handlen den Reset jetzt selbst (handleMarkOne /
  // handleMarkAll). `notificationCount` ist absichtlich KEINE Dep —
  // sonst läuft der Effect bei jedem WS-Push und überschreibt die
  // gerade angekommene neue Notification.
  useEffect(() => {
    if (resetNotificationCount) resetNotificationCount(notificationCount);
  }, [resetNotificationCount]);
  const handleMarkOne = async (id) => {
    await markNotificationAsRead?.(id);
    resetNotificationCount?.(Math.max(0, notificationCount - 1));
  };
  const handleMarkAll = async () => {
    await markAllNotificationsAsRead?.();
    resetNotificationCount?.(0);
  };
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notifications
            {unreadCount > 0 && (
              <Badge variant="secondary" className="ml-1">{unreadCount} neu</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Letzte Plattform-Events: Kommissionen, Markt-Approvals,
            Settlement-Bestätigungen.
          </CardDescription>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleMarkAll}
          >
            <CheckCircle className="h-4 w-4 mr-1" />
            Alle als gelesen
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {notifications.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Bell className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            Keine Benachrichtigungen.
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map((n) => (
              <div
                key={n.id}
                onClick={() => !n.read && handleMarkOne(n.id)}
                className={`flex items-start gap-3 p-3 border rounded-lg transition-colors ${
                  n.read
                    ? 'bg-gray-50 border-gray-100 opacity-70'
                    : 'bg-white border-gray-200 hover:bg-gray-50 cursor-pointer'
                }`}
              >
                <div
                  className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
                    n.read
                      ? 'bg-gray-300'
                      : n.priority === 'high'
                      ? 'bg-red-400'
                      : n.priority === 'medium'
                      ? 'bg-yellow-400'
                      : 'bg-blue-400'
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className={`text-sm font-medium ${n.read ? 'text-gray-600' : 'text-gray-900'}`}>
                      {n.title}
                    </h4>
                    <span className="text-[10px] text-gray-400 whitespace-nowrap dark:text-gray-500">
                      {new Date(n.created_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                    </span>
                  </div>
                  <p className={`text-xs mt-0.5 ${n.read ? 'text-gray-500' : 'text-gray-600'}`}>
                    {n.message}
                  </p>
                </div>
                {!n.read && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-shrink-0 h-7 px-2"
                    onClick={(e) => { e.stopPropagation(); handleMarkOne(n.id); }}
                    title="Als gelesen markieren"
                  >
                    <CheckCircle className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// PlatformNotificationsCard: Audit-Trail aller Notifications,
// die das System an Nutzer geschickt hat. Filter nach User-Suche,
// Type, Priority, Read-Status, Datums-Range.
const PlatformNotificationsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    user_id: '',
    type: '',
    priority: '',
    read: '',
    since_days: 7,
    search: '',
    limit: 50,
    offset: 0,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) params[k] = v;
      });
      const res = await api.admin.getPlatformNotifications(params);
      setData(res.data);
    } catch (e) {
      console.error('platform notifications fetch failed', e);
      setData({ items: [], total: 0, available_types: [] });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateFilter = (k, v) => setFilters(prev => ({ ...prev, [k]: v, offset: 0 }));
  const types = data?.available_types || [];
  const items = data?.items || [];
  const total = data?.total || 0;
  const page = Math.floor((filters.offset || 0) / (filters.limit || 50)) + 1;
  const pages = Math.max(1, Math.ceil(total / (filters.limit || 50)));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Platform Notifications (Audit-Trail)
          <Badge variant="secondary" className="ml-1">{total}</Badge>
        </CardTitle>
        <CardDescription>
          Alle Notifications an alle Nutzer. Default: letzte 7 Tage.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 mb-4 pb-3 border-b border-gray-100 dark:border-slate-800">
          <input
            type="text"
            placeholder="Suchen (Titel oder Nachricht)…"
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="flex-1 min-w-[180px] px-3 py-1.5 text-sm border border-gray-200 rounded-md dark:border-slate-700"
          />
          <input
            type="text"
            placeholder="User-ID (genau)"
            value={filters.user_id}
            onChange={(e) => updateFilter('user_id', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-md w-[200px] dark:border-slate-700"
          />
          <select
            value={filters.type}
            onChange={(e) => updateFilter('type', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-md dark:border-slate-700"
          >
            <option value="">Alle Types</option>
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select
            value={filters.priority}
            onChange={(e) => updateFilter('priority', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-md dark:border-slate-700"
          >
            <option value="">Alle Priorities</option>
            <option value="low">low</option>
            <option value="medium">medium</option>
            <option value="high">high</option>
          </select>
          <select
            value={filters.read}
            onChange={(e) => updateFilter('read', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-md dark:border-slate-700"
          >
            <option value="">Alle</option>
            <option value="false">Ungelesen</option>
            <option value="true">Gelesen</option>
          </select>
          <select
            value={filters.since_days}
            onChange={(e) => updateFilter('since_days', parseInt(e.target.value, 10))}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-md dark:border-slate-700"
          >
            <option value="1">Letzte 24h</option>
            <option value="7">Letzte 7 Tage</option>
            <option value="30">Letzte 30 Tage</option>
            <option value="0">Alle</option>
          </select>
        </div>
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">Lade…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">Keine Treffer.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 dark:bg-slate-800/60">
                  <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">Wann</th>
                  <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">User</th>
                  <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">Type</th>
                  <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">Priority</th>
                  <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">Titel</th>
                  <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((n) => (
                  <tr key={n.id} className="border-b hover:bg-gray-50 dark:hover:bg-slate-800/60">
                    <td className="p-2 text-xs text-gray-500 whitespace-nowrap dark:text-gray-400">
                      {new Date(n.created_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td className="p-2 text-xs">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{n.user_handle || '—'}</div>
                      <div className="text-gray-400 truncate max-w-[180px] dark:text-gray-500">{n.user_email}</div>
                    </td>
                    <td className="p-2">
                      <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs dark:bg-slate-800">{n.type}</span>
                    </td>
                    <td className="p-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        n.priority === 'high' ? 'bg-red-100 text-red-700'
                          : n.priority === 'medium' ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>{n.priority}</span>
                    </td>
                    <td className="p-2">
                      <div className="font-medium text-gray-900 truncate max-w-xs dark:text-gray-100">{n.title}</div>
                      <div className="text-xs text-gray-500 truncate max-w-xs dark:text-gray-400">{n.message}</div>
                    </td>
                    <td className="p-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${n.read ? 'bg-gray-100 text-gray-600' : 'bg-emerald-100 text-emerald-700'}`}>
                        {n.read ? 'gelesen' : 'neu'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between mt-3 text-xs text-gray-500 dark:text-gray-400">
              <span>Seite {page} / {pages} · {total} gesamt</span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={filters.offset === 0}
                  onClick={() => setFilters(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                >
                  ← Vor
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={(filters.offset + filters.limit) >= total}
                  onClick={() => setFilters(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                >
                  Weiter →
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// SentryIssuesCard: Tabelle aller Sentry-Issues des konfigurierten
// Projekts. Backend cached 60s, Filter Status/Level/Environment +
// Search, Permalink direkt zu Sentry. Falls SENTRY_AUTH_TOKEN /
// ORG_SLUG / PROJECT_SLUG nicht gesetzt sind, zeigen wir eine
// Inline-Konfig-Hilfe statt eine leere Tabelle.
const SentryIssuesCard = () => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeProject, setActiveProject] = useState(null);
  const [filters, setFilters] = useState({
    status: 'unresolved',
    level: '',
    environment: '',
    query: '',
    stats_period: '14d',
    limit: 25,
    offset: 0,
  });

  useEffect(() => {
    let cancelled = false;
    api.admin.getSentryStatus().then(r => {
      if (cancelled) return;
      setStatus(r.data);
      const projs = r.data?.projects || [];
      if (projs.length > 0) setActiveProject(projs[0]);
    }).catch(() => {
      if (!cancelled) setStatus({ configured: false, reason: 'request failed' });
    });
    return () => { cancelled = true; };
  }, []);

  const fetchData = useCallback(async () => {
    if (status && status.configured === false) return;
    if (!activeProject) return;
    setLoading(true);
    try {
      const params = { project: activeProject };
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) params[k] = v;
      });
      const res = await api.admin.getSentryIssues(params);
      setData(res.data);
    } catch (e) {
      console.error('sentry issues fetch failed', e);
      const detail = e.response?.data?.detail || e.message;
      setData({ items: [], count: 0, error: detail });
    } finally {
      setLoading(false);
    }
  }, [filters, status, activeProject]);

  useEffect(() => {
    if (status?.configured && activeProject) fetchData();
  }, [fetchData, status, activeProject]);

  // Beim Project-Switch Offset auf 0 zuruecksetzen — sonst fragt
  // man Seite 2 eines anderen Projekts ab, was selten gewollt ist.
  const switchProject = (slug) => {
    setActiveProject(slug);
    setFilters(prev => ({ ...prev, offset: 0 }));
  };

  const updateFilter = (k, v) => setFilters(prev => ({ ...prev, [k]: v, offset: 0 }));
  const items = data?.items || [];
  const hasMore = !!data?.has_more;

  const levelBadge = (lvl) => {
    if (lvl === 'fatal') return 'bg-red-200 text-red-900';
    if (lvl === 'error') return 'bg-red-100 text-red-700';
    if (lvl === 'warning') return 'bg-yellow-100 text-yellow-700';
    if (lvl === 'info') return 'bg-blue-100 text-blue-700';
    if (lvl === 'debug') return 'bg-gray-100 text-gray-600';
    return 'bg-gray-100 text-gray-700';
  };
  const statusBadge = (s) => {
    if (s === 'resolved') return 'bg-emerald-100 text-emerald-700';
    if (s === 'ignored') return 'bg-gray-100 text-gray-600';
    return 'bg-red-100 text-red-700'; // unresolved
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          Sentry Issues
          {data?.count != null && <Badge variant="secondary" className="ml-1">{data.count}{hasMore ? '+' : ''}</Badge>}
          {data?.stale && <Badge variant="outline" className="ml-1 text-yellow-700 border-yellow-300">stale (cache)</Badge>}
        </CardTitle>
        <CardDescription>
          {status?.configured
            ? <>Issues von Sentry-Organisation <code className="text-xs bg-gray-100 px-1 rounded dark:bg-slate-800">{status.org}</code>. Backend cached 60s.</>
            : 'Sentry-Issues-Tabelle (Backend ruft die Sentry-REST-API ab).'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {status?.configured && (status.projects?.length || 0) > 1 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {status.projects.map(p => (
              <button
                key={p}
                type="button"
                onClick={() => switchProject(p)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                  p === activeProject
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-white text-purple-700 border-purple-200 hover:bg-purple-50'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        )}
        {status?.configured === false && (
          <Alert className="mb-4 border-yellow-200 bg-yellow-50 dark:bg-amber-500/10">
            <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
            <AlertDescription className="text-yellow-900">
              <div className="font-medium">Sentry-Integration ist nicht konfiguriert.</div>
              <div className="text-sm mt-1">
                Trage in <code className="text-xs bg-yellow-100 px-1 rounded">backend/.env</code> die drei Variablen ein:
                <ul className="list-disc ml-5 mt-1 space-y-0.5">
                  <li><code className="text-xs">SENTRY_AUTH_TOKEN</code> — Sentry User-Auth-Token mit <code className="text-xs">event:read</code> Scope</li>
                  <li><code className="text-xs">SENTRY_ORG_SLUG</code> — z.B. <code className="text-xs">anyframe-llc</code></li>
                  <li><code className="text-xs">SENTRY_PROJECT_SLUGS</code> — kommagetrennt, z.B. <code className="text-xs">predmarket-backend,predmarket-frontend</code></li>
                </ul>
                Dann uvicorn neu starten und die Seite reloaden.
              </div>
            </AlertDescription>
          </Alert>
        )}

        {status?.configured && (
          <>
            <div className="flex flex-wrap gap-2 mb-4 pb-3 border-b border-gray-100 dark:border-slate-800">
              <input
                type="text"
                placeholder="Sentry-Query (z.B. is:unresolved level:error)…"
                value={filters.query}
                onChange={(e) => updateFilter('query', e.target.value)}
                className="flex-1 min-w-[200px] px-3 py-1.5 text-sm border border-gray-200 rounded-md font-mono dark:border-slate-700"
              />
              <select
                value={filters.status}
                onChange={(e) => updateFilter('status', e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-md dark:border-slate-700"
              >
                <option value="unresolved">Unresolved</option>
                <option value="resolved">Resolved</option>
                <option value="ignored">Ignored</option>
                <option value="">Alle</option>
              </select>
              <select
                value={filters.level}
                onChange={(e) => updateFilter('level', e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-md dark:border-slate-700"
              >
                <option value="">Alle Levels</option>
                <option value="fatal">fatal</option>
                <option value="error">error</option>
                <option value="warning">warning</option>
                <option value="info">info</option>
                <option value="debug">debug</option>
              </select>
              <input
                type="text"
                placeholder="Environment"
                value={filters.environment}
                onChange={(e) => updateFilter('environment', e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-md w-[140px] dark:border-slate-700"
              />
              <select
                value={filters.stats_period}
                onChange={(e) => updateFilter('stats_period', e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-md dark:border-slate-700"
              >
                <option value="24h">Letzte 24h</option>
                <option value="14d">Letzte 14 Tage</option>
                <option value="">Alle Zeit</option>
              </select>
              <Button size="sm" variant="outline" onClick={fetchData}>Reload</Button>
            </div>

            {loading ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">Lade…</div>
            ) : data?.error ? (
              <Alert className="border-red-200 bg-red-50 dark:bg-rose-500/10 dark:border-rose-500/30">
                <AlertCircle className="h-4 w-4 text-red-600 dark:text-rose-400" />
                <AlertDescription className="text-red-900">{data.error}</AlertDescription>
              </Alert>
            ) : items.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">Keine Issues mit aktuellen Filtern.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50 dark:bg-slate-800/60">
                      <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">Titel</th>
                      <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">Level</th>
                      <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">Status</th>
                      <th className="text-right p-2 font-medium text-gray-600 dark:text-gray-400">Events</th>
                      <th className="text-right p-2 font-medium text-gray-600 dark:text-gray-400">User</th>
                      <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">Last Seen</th>
                      <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id} className="border-b hover:bg-gray-50 dark:hover:bg-slate-800/60">
                        <td className="p-2 max-w-md">
                          <div className="font-medium text-gray-900 truncate dark:text-gray-100" title={it.title}>{it.title}</div>
                          {it.culprit && (
                            <div className="text-xs text-gray-500 truncate font-mono dark:text-gray-400" title={it.culprit}>{it.culprit}</div>
                          )}
                          {it.short_id && (
                            <div className="text-[10px] text-gray-400 font-mono mt-0.5 dark:text-gray-500">{it.short_id}</div>
                          )}
                        </td>
                        <td className="p-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${levelBadge(it.level)}`}>{it.level || '—'}</span>
                          {it.is_unhandled && (
                            <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] bg-orange-100 text-orange-700">unhandled</span>
                          )}
                        </td>
                        <td className="p-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${statusBadge(it.status)}`}>{it.status || '—'}</span>
                        </td>
                        <td className="p-2 text-right tabular-nums">{(it.count ?? 0).toLocaleString('de-DE')}</td>
                        <td className="p-2 text-right tabular-nums">{(it.user_count ?? 0).toLocaleString('de-DE')}</td>
                        <td className="p-2 text-xs text-gray-500 whitespace-nowrap dark:text-gray-400">
                          {it.last_seen ? new Date(it.last_seen).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                        </td>
                        <td className="p-2">
                          {it.permalink && (
                            <a
                              href={it.permalink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline whitespace-nowrap dark:text-blue-400"
                            >
                              In Sentry →
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between mt-3 text-xs text-gray-500 dark:text-gray-400">
                  <span>
                    {filters.offset + 1} – {filters.offset + items.length}
                    {hasMore && ' (mehr verfügbar)'}
                    {data?.cached_until && ` · Cache bis ${new Date(data.cached_until * 1000).toLocaleTimeString('de-DE')}`}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={filters.offset === 0}
                      onClick={() => setFilters(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                    >
                      ← Vor
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!hasMore}
                      onClick={() => setFilters(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                    >
                      Weiter →
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};


// ActivityLogsCard: Audit-Trail aller User-Aktivitaeten (Login,
// Logout, Register, Password-Change). Zeigt IP + User-Agent +
// optional Geo-Lookup. Filter nach User, Action, IP-Substring.
const ActivityLogsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    user_id: '',
    action: '',
    ip: '',
    since_days: 7,
    search: '',
    limit: 50,
    offset: 0,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) params[k] = v;
      });
      const res = await api.admin.getActivityLogs(params);
      setData(res.data);
    } catch (e) {
      console.error('activity logs fetch failed', e);
      setData({ items: [], total: 0, available_actions: [] });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateFilter = (k, v) => setFilters(prev => ({ ...prev, [k]: v, offset: 0 }));
  const actions = data?.available_actions || [];
  const items = data?.items || [];
  const total = data?.total || 0;
  const page = Math.floor((filters.offset || 0) / (filters.limit || 50)) + 1;
  const pages = Math.max(1, Math.ceil(total / (filters.limit || 50)));

  const actionBadge = (a) => {
    if (a === 'login_success') return 'bg-emerald-100 text-emerald-700';
    if (a === 'login_failed') return 'bg-red-100 text-red-700';
    if (a === 'logout') return 'bg-gray-100 text-gray-600';
    if (a === 'register') return 'bg-blue-100 text-blue-700';
    if (a === 'password_changed') return 'bg-yellow-100 text-yellow-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Activity Logs
          <Badge variant="secondary" className="ml-1">{total}</Badge>
        </CardTitle>
        <CardDescription>
          Audit-Trail: wer hat sich wann und wo eingeloggt. IP + User-Agent + Geo (falls auflösbar).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 mb-4 pb-3 border-b border-gray-100 dark:border-slate-800">
          <input
            type="text"
            placeholder="Action-Substring…"
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="flex-1 min-w-[160px] px-3 py-1.5 text-sm border border-gray-200 rounded-md dark:border-slate-700"
          />
          <input
            type="text"
            placeholder="User-ID (genau)"
            value={filters.user_id}
            onChange={(e) => updateFilter('user_id', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-md w-[180px] dark:border-slate-700"
          />
          <input
            type="text"
            placeholder="IP enthält…"
            value={filters.ip}
            onChange={(e) => updateFilter('ip', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-md w-[140px] dark:border-slate-700"
          />
          <select
            value={filters.action}
            onChange={(e) => updateFilter('action', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-md dark:border-slate-700"
          >
            <option value="">Alle Actions</option>
            {actions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select
            value={filters.since_days}
            onChange={(e) => updateFilter('since_days', parseInt(e.target.value, 10))}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-md dark:border-slate-700"
          >
            <option value="1">Letzte 24h</option>
            <option value="7">Letzte 7 Tage</option>
            <option value="30">Letzte 30 Tage</option>
            <option value="0">Alle</option>
          </select>
        </div>
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">Lade…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">Keine Treffer.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 dark:bg-slate-800/60">
                  <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">Wann</th>
                  <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">User</th>
                  <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">Action</th>
                  <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">IP / Geo</th>
                  <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">User-Agent</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const det = r.details || {};
                  const geo = det.geo || {};
                  const geoLabel = det.is_private
                    ? '(privat)'
                    : [geo.city, geo.country].filter(Boolean).join(', ');
                  return (
                    <tr key={r.id} className="border-b hover:bg-gray-50 dark:hover:bg-slate-800/60">
                      <td className="p-2 text-xs text-gray-500 whitespace-nowrap dark:text-gray-400">
                        {new Date(r.created_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                      </td>
                      <td className="p-2 text-xs">
                        <div className="font-medium text-gray-900 dark:text-gray-100">{r.user_handle || '—'}</div>
                        <div className="text-gray-400 truncate max-w-[180px] dark:text-gray-500">{r.user_email}</div>
                      </td>
                      <td className="p-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${actionBadge(r.action)}`}>{r.action}</span>
                      </td>
                      <td className="p-2 text-xs">
                        <div className="font-mono text-gray-900 dark:text-gray-100">{det.ip || '—'}</div>
                        {geoLabel && <div className="text-gray-500 dark:text-gray-400">{geoLabel}</div>}
                      </td>
                      <td className="p-2 text-xs text-gray-500 max-w-xs truncate dark:text-gray-400" title={det.user_agent}>
                        {det.user_agent || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex items-center justify-between mt-3 text-xs text-gray-500 dark:text-gray-400">
              <span>Seite {page} / {pages} · {total} gesamt</span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={filters.offset === 0}
                  onClick={() => setFilters(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                >
                  ← Vor
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={(filters.offset + filters.limit) >= total}
                  onClick={() => setFilters(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                >
                  Weiter →
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Credit-Sales-Übersicht: zieht aus /admin/credit-purchases und
// listet jeden €-Kauf mit User, VC-Gutschrift und Zeit.
const CreditSalesCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.admin.getCreditPurchases({ limit: 200 });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData({ purchases: [], count: 0, total_eur: 0, total_vc_credited: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Credit Sales
          </CardTitle>
          <CardDescription>
            Reale Credit-Käufe (€). Quelle: platform_fee_transactions
            mit fee_type=credit_sale_eur.
          </CardDescription>
        </div>
        {data && (
          <div className="text-right text-sm">
            <div className="text-emerald-600 font-semibold dark:text-emerald-400">
              € {data.total_eur.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-gray-500 text-xs dark:text-gray-400">
              {data.total_vc_credited.toLocaleString()} VC · {data.count} Käufe
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">Lädt…</div>
        ) : !data || data.purchases.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <DollarSign className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            Noch keine Credit-Käufe.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 dark:bg-slate-800/60">
                  <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">User</th>
                  <th className="text-right p-2 font-medium text-gray-600 dark:text-gray-400">€</th>
                  <th className="text-right p-2 font-medium text-gray-600 dark:text-gray-400">VC</th>
                  <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">Wann</th>
                </tr>
              </thead>
              <tbody>
                {data.purchases.map((p) => (
                  <tr key={p.id} className="border-b hover:bg-gray-50 dark:hover:bg-slate-800/60">
                    <td className="p-2">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{p.user_handle || '—'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{p.user_email || ''}</div>
                    </td>
                    <td className="p-2 text-right font-semibold text-emerald-600 dark:text-emerald-400">
                      € {p.eur_amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="p-2 text-right text-gray-700 dark:text-gray-300">
                      {p.vc_credited.toLocaleString()} VC
                    </td>
                    <td className="p-2 text-xs text-gray-500 whitespace-nowrap dark:text-gray-400">
                      {p.created_at ? new Date(p.created_at).toLocaleString('de-DE', {
                        day: '2-digit', month: '2-digit', year: '2-digit',
                        hour: '2-digit', minute: '2-digit', hour12: false,
                      }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


const AdminPage = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [platformRevenue, setPlatformRevenue] = useState(null);
  const [markets, setMarkets] = useState([]);
  // Markets-Tab filter UI state. Status/category dropdowns + a free
  // text search on the title; the list is filtered client-side from
  // the already-fetched `markets` array so the filter feels instant.
  const [marketStatusFilter, setMarketStatusFilter] = useState('all');
  const [marketCategoryFilter, setMarketCategoryFilter] = useState('all');
  const [marketTypeFilter, setMarketTypeFilter] = useState('all');
  const [marketBoostFilter, setMarketBoostFilter] = useState('all'); // all | boosted
  const [marketSearch, setMarketSearch] = useState('');
  // null = ungeordnet (Default-Reihenfolge der API), 'asc' | 'desc' = nach closes_at sortiert.
  // Klick auf den Header zykelt null → asc → desc → null.
  const [marketCloseSort, setMarketCloseSort] = useState(null);
  // Initialer Tab kann via ?tab=... vorgegeben werden — z.B. der
  // Bell-Klick im Header schickt Admins direkt nach /admin?tab=notifications.
  const location = useLocation();
  const initialTab = (() => {
    const t = new URLSearchParams(location.search).get('tab');
    const valid = ADMIN_NAV_SECTIONS.flatMap(s => s.items.map(i => i.value));
    return t && valid.includes(t) ? t : 'dashboard';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => {
    const t = new URLSearchParams(location.search).get('tab');
    const valid = ADMIN_NAV_SECTIONS.flatMap(s => s.items.map(i => i.value));
    if (t && valid.includes(t)) setActiveTab(t);
  }, [location.search]);
  const [loading, setLoading] = useState(true);
  
  // Create Market Form State
  const [newMarket, setNewMarket] = useState({
    title: '',
    description: '',
    mode: 'prediction',
    category: '',
    civic_category: '',
    closes_at: '',
    image_url: '',
  });
  const CIVIC_CATEGORIES = ['Technologie', 'Umwelt', 'Gesellschaft', 'Wirtschaft', 'Politik'];
  
  // Settlement Form State
  const [settlementData, setSettlementData] = useState({
    market_id: '',
    winning_outcome_id: ''
  });
  const [marketOutcomes, setMarketOutcomes] = useState({});
  
  // Edit Market State
  const [editMarket, setEditMarket] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editMarketData, setEditMarketData] = useState({
    title: '',
    description: '',
    category: '',
    closes_at: '',
    image_url: '',
  });
  
  // Categories State
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    color: '#22c55e',
    icon: 'Trophy'
  });
  
  // Virtual Shop Management — these are the cosmetics users see in
  // the user-facing Shop page (Avatar-Emoji, Profilrahmen, …). The
  // physical shop catalog was removed from the admin UI; backend
  // endpoints for it stay around so legacy data isn't broken.
  const [virtualShopItems, setVirtualShopItems] = useState([]);
  const [virtualShopForm, setVirtualShopForm] = useState({
    id: '',
    name: '',
    description: '',
    price: 100,
    category: 'avatar',
    tier: 'bronze',
    effect: '',
    image_url: '',
  });
  const [editingVirtualItemId, setEditingVirtualItemId] = useState(null);

  // Credit Packages State
  const [creditPackages, setCreditPackages] = useState([]);
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageFormData, setPackageFormData] = useState({
    name: '',
    credits: 100,
    bonus_credits: 0,
    price: 10.00,
    popular: false,
    active: true,
    display_order: 0
  });

  // Creator Management State
  const [creatorApplications, setCreatorApplications] = useState([]);
  const [pendingMarkets, setPendingMarkets] = useState([]);
  const [charities, setCharities] = useState([]);
  const [showCharityDialog, setShowCharityDialog] = useState(false);
  const [editingCharity, setEditingCharity] = useState(null);
  const [charityFormData, setCharityFormData] = useState({
    name: '',
    description: '',
    url: '',
    logo_url: '',
    status: 'active'
  });

  // Betting Management State
  const [activeBets, setActiveBets] = useState([]);
  const [bettingStats, setBettingStats] = useState(null);
  const [selectedBet, setSelectedBet] = useState(null);
  const [showBetDetailsDialog, setShowBetDetailsDialog] = useState(false);

  // Shop Categories Management State
  const [shopCategories, setShopCategories] = useState([]);
  const [showNewCategoryDialog, setShowNewCategoryDialog] = useState(false);
  const [newCategoryData, setNewCategoryData] = useState({
    name: '',
    type: 'physical'
  });

  // Commission Settings State
  const [commissionSettings, setCommissionSettings] = useState({
    creator_commission_rate: 0.05,
    referral_l1_rate: 0.05,
    referral_l2_rate: 0.02
  });
  const [showCommissionDialog, setShowCommissionDialog] = useState(false);
  const [commissionFormData, setCommissionFormData] = useState({
    creator_commission_rate: 0.05,
    referral_l1_rate: 0.05,
    referral_l2_rate: 0.02
  });

  // Fee Settings State
  const [feeSettings, setFeeSettings] = useState({
    trading_fee_rate: 0.01,
    settlement_spread_rate: 0.02,
    platform_fee_rate: 0.005,
    minimum_fee_amount: 0.1,
    maximum_fee_amount: 100.0
  });
  const [showFeeDialog, setShowFeeDialog] = useState(false);
  const [feeAnalytics, setFeeAnalytics] = useState(null);
  const [winLossAnalytics, setWinLossAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Chart Data State
  const [bettingTrends, setBettingTrends] = useState([]);
  const [categoryDistribution, setCategoryDistribution] = useState([]);
  const [topMarketsChart, setTopMarketsChart] = useState([]);
  const [userGrowth, setUserGrowth] = useState([]);
  const [chartsLoading, setChartsLoading] = useState(false);
  
  // User Management State
  const [users, setUsers] = useState([]);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  
  // Create User State
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [newUserData, setNewUserData] = useState({
    handle: '',
    email: '',
    password: '',
    roles: ['user']
  });
  
  // editUserData moved to separate EditUserDialog component

  useEffect(() => {
    if (user && user.roles && user.roles.includes('admin')) {
      fetchAdminData();
    }
  }, [user]);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      
      // Fetch data with individual error handling instead of Promise.all
      const results = {};
      
      try {
        const dashboardResponse = await api.admin.getDashboard();
        results.dashboard = dashboardResponse.data;
      } catch (error) {
        console.error('Error fetching dashboard:', error);
        results.dashboard = null;
      }

      try {
        const revRes = await api.admin.getPlatformRevenue();
        setPlatformRevenue(revRes.data);
      } catch (error) {
        // Endpoint optional — alt-Backend ohne den Endpoint soll
        // den Dashboard nicht zerschiessen.
        setPlatformRevenue(null);
      }
      
      try {
        const marketsResponse = await api.markets.getAll();
        results.markets = marketsResponse.data;
      } catch (error) {
        console.error('Error fetching markets:', error);
        results.markets = [];
      }
      
      try {
        const categoriesResponse = await api.admin.getCategories();
        results.categories = categoriesResponse.data;
      } catch (error) {
        console.error('Error fetching categories:', error);
        results.categories = [];
      }

      try {
        const virtualShopResponse = await api.admin.getVirtualShopItems();
        results.virtualShopItems = virtualShopResponse.data;
      } catch (error) {
        console.error('Error fetching virtual shop items:', error);
        results.virtualShopItems = [];
      }

      try {
        const creditPackagesResponse = await api.admin.getCreditPackages();
        results.creditPackages = creditPackagesResponse.data;
      } catch (error) {
        console.error('Error fetching credit packages:', error);
        results.creditPackages = [];
      }
      
      try {
        const usersResponse = await api.admin.getUsers();
        results.users = usersResponse.data;
      } catch (error) {
        console.error('Error fetching users:', error);
        results.users = { users: [] };
      }
      
      try {
        const analyticsResponse = await api.admin.getUserAnalytics();
        results.analytics = analyticsResponse.data;
      } catch (error) {
        console.error('Error fetching analytics:', error);
        results.analytics = null;
      }

      try {
        const applicationsResponse = await api.admin.getCreatorApplications();
        results.creatorApplications = applicationsResponse.data;
      } catch (error) {
        console.error('Error fetching creator applications:', error);
        results.creatorApplications = [];
      }

      try {
        const pendingMarketsResponse = await api.admin.getPendingMarkets();
        results.pendingMarkets = pendingMarketsResponse.data;
      } catch (error) {
        console.error('Error fetching pending markets:', error);
        results.pendingMarkets = [];
      }

      try {
        const charitiesResponse = await api.admin.getCharities();
        results.charities = charitiesResponse.data;
      } catch (error) {
        console.error('Error fetching charities:', error);
        results.charities = [];
      }

      try {
        const activeBetsResponse = await api.admin.getAllBets();
        results.activeBets = activeBetsResponse.data;
      } catch (error) {
        console.error('Error fetching active bets:', error);
        results.activeBets = [];
      }

      try {
        const bettingStatsResponse = await api.admin.getBettingStats();
        results.bettingStats = bettingStatsResponse.data;
      } catch (error) {
        console.error('Error fetching betting stats:', error);
        results.bettingStats = null;
      }

      try {
        const commissionSettingsResponse = await api.admin.getCommissionSettings();
        results.commissionSettings = commissionSettingsResponse.data;
      } catch (error) {
        console.error('Error fetching commission settings:', error);
        results.commissionSettings = null;
      }

      try {
        const feeSettingsResponse = await api.admin.getFeeSettings();
        results.feeSettings = feeSettingsResponse.data;
      } catch (error) {
        console.error('Error fetching fee settings:', error);
        results.feeSettings = null;
      }
      
      // Update state with fetched data
      setDashboardData(results.dashboard);
      setMarkets(results.markets);
      setCategories(results.categories);
      setVirtualShopItems(results.virtualShopItems);
      setCreditPackages(results.creditPackages);
      setUsers(results.users.users || []);
      setUserAnalytics(results.analytics);
      setCreatorApplications(results.creatorApplications);
      setPendingMarkets(results.pendingMarkets);
      setCharities(results.charities);
      setActiveBets(results.activeBets);
      setBettingStats(results.bettingStats);
      if (results.commissionSettings) {
        setCommissionSettings(results.commissionSettings);
      }
      if (results.feeSettings) {
        setFeeSettings(results.feeSettings);
      }

      // Fetch outcomes for all markets
      const outcomesResponse = await api.admin.getOutcomesBulk();
      setMarketOutcomes(outcomesResponse.data);

      // Fetch chart data in parallel
      fetchChartData();
      
    } catch (error) {
      console.error('Error in fetchAdminData:', error);
      toast.error('Failed to load some admin data. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    setChartsLoading(true);
    try {
      const [trendsRes, categoryRes, topMarketsRes, userGrowthRes] = await Promise.all([
        api.admin.getBettingTrends(30).catch(() => ({ data: [] })),
        api.admin.getCategoryDistribution().catch(() => ({ data: [] })),
        api.admin.getTopMarkets(8).catch(() => ({ data: [] })),
        api.admin.getUserGrowth(30).catch(() => ({ data: [] }))
      ]);
      
      setBettingTrends(trendsRes.data);
      setCategoryDistribution(categoryRes.data);
      setTopMarketsChart(topMarketsRes.data);
      setUserGrowth(userGrowthRes.data);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    } finally {
      setChartsLoading(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    
    try {
      await api.admin.createCategory(newCategory);
      toast.success('Category created successfully!');
      
      // Reset form
      setNewCategory({
        name: '',
        description: '',
        color: '#22c55e',
        icon: 'Trophy'
      });
      
      // Refresh data
      fetchAdminData();
      
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('Failed to create category: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.admin.deleteCategory(categoryId);
      toast.success('Category deleted successfully!');
      fetchAdminData();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Virtual Shop Item Handlers
  const resetVirtualShopForm = () => {
    setVirtualShopForm({
      id: '',
      name: '',
      description: '',
      price: 100,
      category: 'avatar',
      tier: 'bronze',
      effect: '',
      image_url: '',
    });
    setEditingVirtualItemId(null);
  };

  // Convert a chosen file to a base64 data: URI so admins can attach
  // small cover images without us standing up an upload service.
  // Hard cap at 1 MB — anything bigger should live behind a CDN URL
  // pasted into the URL field instead.
  const handleVirtualShopImageFile = (file) => {
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error('Bild zu gross (max 1 MB). Lade es auf einen CDN und nutze die URL.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setVirtualShopForm((f) => ({ ...f, image_url: String(reader.result) }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitVirtualShopItem = async (e) => {
    e.preventDefault();
    try {
      if (editingVirtualItemId) {
        await api.admin.updateVirtualShopItem(editingVirtualItemId, virtualShopForm);
        toast.success('Virtual item updated');
      } else {
        await api.admin.createVirtualShopItem(virtualShopForm);
        toast.success('Virtual item created');
      }
      resetVirtualShopForm();
      fetchAdminData();
    } catch (error) {
      console.error('Error saving virtual shop item:', error);
      toast.error('Save failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditVirtualShopItem = (item) => {
    setEditingVirtualItemId(item.id);
    setVirtualShopForm({
      id: item.id || '',
      name: item.name || '',
      description: item.description || '',
      price: Number(item.price) || 0,
      category: item.category || 'avatar',
      tier: item.tier || 'bronze',
      effect: item.effect || '',
      image_url: item.image_url || '',
    });
  };

  const handleDeleteVirtualShopItem = async (itemId) => {
    if (!window.confirm('Delete this virtual item? Existing user_items rows that reference it stay around.')) return;
    try {
      await api.admin.deleteVirtualShopItem(itemId);
      toast.success('Virtual item deleted');
      if (editingVirtualItemId === itemId) resetVirtualShopForm();
      fetchAdminData();
    } catch (error) {
      console.error('Error deleting virtual shop item:', error);
      toast.error('Delete failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Credit Package Handlers
  const handleOpenPackageDialog = (packageToEdit = null) => {
    if (packageToEdit) {
      setEditingPackage(packageToEdit);
      setPackageFormData({
        name: packageToEdit.name,
        credits: packageToEdit.credits,
        bonus_credits: packageToEdit.bonus_credits,
        price: packageToEdit.price,
        popular: packageToEdit.popular,
        active: packageToEdit.active,
        display_order: packageToEdit.display_order
      });
    } else {
      setEditingPackage(null);
      setPackageFormData({
        name: '',
        credits: 100,
        bonus_credits: 0,
        price: 10.00,
        popular: false,
        active: true,
        display_order: creditPackages.length
      });
    }
    setShowPackageDialog(true);
  };

  const handleSavePackage = async (e) => {
    e.preventDefault();
    
    try {
      if (editingPackage) {
        // Update existing package
        await api.admin.updateCreditPackage(editingPackage.id, packageFormData);
        toast.success('Credit package updated successfully!');
      } else {
        // Create new package
        await api.admin.createCreditPackage(packageFormData);
        toast.success('Credit package created successfully!');
      }
      
      setShowPackageDialog(false);
      setEditingPackage(null);
      fetchAdminData();
    } catch (error) {
      console.error('Error saving credit package:', error);
      toast.error('Failed to save credit package: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this credit package?')) {
      return;
    }
    
    try {
      await api.admin.deleteCreditPackage(packageId);
      toast.success('Credit package deleted successfully!');
      fetchAdminData();
    } catch (error) {
      console.error('Error deleting credit package:', error);
      toast.error('Failed to delete credit package: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleReorderPackage = async (packageId, newOrder) => {
    try {
      await api.admin.reorderCreditPackage(packageId, { new_order: newOrder });
      toast.success('Package order updated!');
      fetchAdminData();
    } catch (error) {
      console.error('Error reordering package:', error);
      toast.error('Failed to reorder package');
    }
  };

  const handleViewUserDetails = async (userId) => {
    try {
      const response = await api.admin.getUser(userId);
      setShowEditUserDialog(false);
      setSelectedUser(response.data);
      setShowUserDialog(true);
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error('Failed to load user details');
    }
  };

  const handleEditUser = (user) => {
    try {
      const userData = user?.user || user;

      if (!userData || !userData.id) {
        toast.error('User ID is missing');
        return;
      }

      const sanitizedUser = {
        id: userData.id,
        handle: userData.handle || '',
        email: userData.email || '',
        roles: Array.isArray(userData.roles) ? userData.roles : ['user'],
        is_active: userData.is_active !== false,
        created_at: userData.created_at
      };

      setShowUserDialog(false);
      setSelectedUser(sanitizedUser);
      setShowEditUserDialog(true);
    } catch (error) {
      console.error('Error in handleEditUser:', error);
      toast.error('Failed to open edit dialog: ' + error.message);
    }
  };

  const handleOpenCreateUserDialog = () => {
    setNewUserData({
      handle: '',
      email: '',
      password: '',
      roles: ['user']
    });
    setShowCreateUserDialog(true);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUserData.handle || !newUserData.email || !newUserData.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (newUserData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    
    try {
      // Admin-Endpoint /admin/users statt /auth/register, damit das
      // Auth-Cookie nicht auf den neuen User überschrieben wird.
      await api.admin.createUser({
        handle: newUserData.handle,
        email: newUserData.email,
        password: newUserData.password,
        roles: newUserData.roles?.length ? newUserData.roles : ['user'],
      });

      toast.success('User created successfully!');
      setShowCreateUserDialog(false);
      fetchAdminData(); // Refresh the user list
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUserRoleChange = (role, checked) => {
    setNewUserData(prev => ({
      ...prev,
      roles: checked 
        ? [...prev.roles, role]
        : prev.roles.filter(r => r !== role)
    }));
  };

  // handleUpdateUser moved to separate EditUserDialog component

  const handleAdjustCredits = async (userId, amount, reason) => {
    try {
      const response = await api.admin.updateUserCredits(userId, { amount, reason });
      toast.success(`Credits adjusted! New balance: ${response.data.new_balance}`);
      fetchAdminData();

      // Refresh user details if dialog is open
      if (selectedUser && selectedUser.user?.id === userId) {
        handleViewUserDetails(userId);
      }
    } catch (error) {
      console.error('Error adjusting credits:', error);
      toast.error('Failed to adjust credits: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteUser = async (userId, userHandle) => {
    if (!window.confirm(`Are you sure you want to delete user "${userHandle}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await api.admin.deleteUser(userId);
      toast.success('User deleted successfully!');
      fetchAdminData();
      if (showUserDialog) {
        setShowUserDialog(false);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleResetPassword = async (userId, userHandle) => {
    const newPassword = prompt(`Enter new password for user "${userHandle}":`);
    if (!newPassword) return;
    
    if (newPassword.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    try {
      await api.admin.resetUserPassword(userId, { new_password: newPassword });
      toast.success('Password reset successfully! User muss beim nächsten Login ein neues Passwort setzen.');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Failed to reset password: ' + (error.response?.data?.detail || error.message));
    }
  };

  const filteredUsers = users.filter(user => {
    // Add null/undefined checks to prevent errors
    if (!user || !user.handle || !user.email || !user.roles) {
      return false;
    }
    
    const matchesSearch = user.handle.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(userSearchTerm.toLowerCase());
    const matchesRole = !userRoleFilter || user.roles.includes(userRoleFilter);
    return matchesSearch && matchesRole;
  });

  const handleCreateMarket = async (e) => {
    e.preventDefault();

    try {
      const isCivic = newMarket.mode === 'civic';
      if (isCivic && !newMarket.civic_category) {
        toast.error('Civic-Kategorie wählen');
        return;
      }
      if (!isCivic && !newMarket.category) {
        toast.error('Kategorie wählen');
        return;
      }

      const marketData = {
        title: newMarket.title,
        description: newMarket.description,
        mode: newMarket.mode,
        category: isCivic ? null : newMarket.category,
        civic_category: isCivic ? newMarket.civic_category : null,
        closes_at: new Date(newMarket.closes_at).toISOString(),
        image_url: newMarket.image_url || null,
      };

      await api.markets.create(marketData);
      toast.success(isCivic ? 'Civic-Markt erstellt!' : 'Market created successfully!');

      // Outcomes (YES/NO) legt das Backend in /admin/markets fuer
      // Prediction-Maerkte automatisch an. Civic-Maerkte brauchen
      // keine Outcome-Rows (Tally laeuft auf market.yes_votes /
      // no_votes). Frueher hat das Frontend hier zusaetzlich
      // createOutcome aufgerufen -> doppelte YES/NO-Outcomes pro
      // Markt (siehe z.B. Banksy mit 4 outcomes statt 2).

      setNewMarket({
        title: '',
        description: '',
        mode: 'prediction',
        category: '',
        civic_category: '',
        closes_at: '',
        image_url: '',
      });

      fetchAdminData();
    } catch (error) {
      console.error('Error creating market:', error);
      toast.error('Failed to create market: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateMarketStatus = async (marketId, newStatus) => {
    try {
      await api.admin.updateMarketStatus(marketId, newStatus);
      toast.success(`Market ${newStatus} successfully!`);
      fetchAdminData();
    } catch (error) {
      console.error('Error updating market status:', error);
      toast.error('Failed to update market status');
    }
  };

  const handleDeleteMarket = async (marketId, marketTitle) => {
    if (!window.confirm(`Are you sure you want to delete the market "${marketTitle}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await api.admin.deleteMarket(marketId);
      toast.success('Market deleted successfully!');
      fetchAdminData();
    } catch (error) {
      console.error('Error deleting market:', error);
      toast.error('Failed to delete market: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleOpenEditMarket = (market) => {
    setEditMarket(market);
    setEditMarketData({
      title: market.title,
      description: market.description,
      category: market.category,
      closes_at: new Date(market.closes_at).toISOString().slice(0, 16), // Format for datetime-local input
      image_url: market.image_url || '',
    });
    setShowEditDialog(true);
  };

  const handleEditMarketSubmit = async (e) => {
    e.preventDefault();
    
    if (!editMarket) return;
    
    try {
      const updateData = {
        title: editMarketData.title,
        description: editMarketData.description,
        category: editMarketData.category,
        closes_at: new Date(editMarketData.closes_at).toISOString(),
        image_url: editMarketData.image_url || null,
      };
      
      const response = await api.admin.updateMarket(editMarket.id, updateData);
      
      if (response.data.active_bets_restriction) {
        toast.success('Market end time updated successfully! (Other fields restricted due to active bets)');
      } else {
        toast.success('Market updated successfully!');
      }
      
      setShowEditDialog(false);
      setEditMarket(null);
      fetchAdminData();
    } catch (error) {
      console.error('Error updating market:', error);
      toast.error('Failed to update market: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Manually close a market
  const handleCloseMarket = async (marketId) => {
    if (!window.confirm('Are you sure you want to close this market? Users will no longer be able to place bets.')) {
      return;
    }
    
    try {
      await api.admin.updateMarketStatus(marketId, 'closed');
      toast.success('Market closed successfully!');
      fetchAdminData();
    } catch (error) {
      console.error('Error closing market:', error);
      toast.error('Failed to close market: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Reopen a market
  const handleReopenMarket = async (marketId) => {
    if (!window.confirm('Are you sure you want to reopen this market? Users will be able to place bets again.')) {
      return;
    }
    
    try {
      await api.admin.updateMarketStatus(marketId, 'open');
      toast.success('Market reopened successfully!');
      fetchAdminData();
    } catch (error) {
      console.error('Error reopening market:', error);
      toast.error('Failed to reopen market: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Admin-Override: Markt boosten/entboosten (ohne Creator-Boost-Item).
  const handleToggleBoost = async (marketId, makeBoosted) => {
    try {
      await api.admin.setMarketBoost(marketId, makeBoosted, 7);
      toast.success(makeBoosted ? 'Markt geboostet (7 Tage)' : 'Boost entfernt');
      fetchAdminData();
    } catch (error) {
      toast.error('Boost-Änderung fehlgeschlagen: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSettleMarket = async (e) => {
    e.preventDefault();

    if (!settlementData.market_id || !settlementData.winning_outcome_id) {
      toast.error('Please select both market and winning outcome');
      return;
    }
    
    try {
      const response = await api.admin.settleMarket(settlementData.market_id, settlementData.winning_outcome_id);
      const d = response.data;
      if (d.is_one_sided_refund) {
        toast.success(`Market settled (Parimutuel). One-sided: ${d.winning_bets_count} bet(s) refunded.`);
      } else {
        toast.success(`Market settled (Parimutuel). Pool: ${d.total_pool?.toFixed(0)}, Payout: ${d.total_payout?.toFixed(1)}, Fee: ${d.platform_fee?.toFixed(1)}`);
      }
      
      setSettlementData({
        market_id: '',
        winning_outcome_id: ''
      });
      
      fetchAdminData();
    } catch (error) {
      console.error('Error settling market:', error);
      toast.error('Failed to settle market');
    }
  };

  // Creator Management Handlers
  const reviewCreatorApplication = async (applicationId, status) => {
    try {
      const adminNotes = status === 'rejected' ? prompt('Rejection reason (optional):') : '';
      
      await api.admin.updateCreatorApplication(applicationId, {
        status: status,
        admin_notes: adminNotes
      });
      
      toast.success(`Application ${status} successfully!`);
      fetchAdminData();
    } catch (error) {
      console.error('Error reviewing application:', error);
      toast.error('Failed to review application');
    }
  };

  const approveMarket = async (marketId, status, rejectionReason = '') => {
    try {
      await api.admin.approveMarket(marketId, {
        approval_status: status,
        rejection_reason: rejectionReason
      });
      
      toast.success(`Market ${status} successfully!`);
      fetchAdminData();
    } catch (error) {
      console.error('Error approving market:', error);
      toast.error('Failed to update market status');
    }
  };


  // Shop Category Management Handlers
  const handleCreateShopCategory = async (e) => {
    e.preventDefault();
    
    try {
      // For now, we'll store categories locally since there's no backend endpoint
      // In a real implementation, this would be a backend API call
      const newCategory = {
        id: Date.now().toString(),
        name: newCategoryData.name,
        type: newCategoryData.type,
        created_at: new Date().toISOString()
      };
      
      setShopCategories(prev => [...prev, newCategory]);
      toast.success('Category created successfully!');
      
      setShowNewCategoryDialog(false);
      setNewCategoryData({
        name: '',
        type: 'physical'
      });
      
    } catch (error) {
      console.error('Error creating shop category:', error);
      toast.error('Failed to create shop category');
    }
  };

  // Commission Settings Handlers
  const handleEditCommissionSettings = () => {
    setCommissionFormData({
      creator_commission_rate: commissionSettings.creator_commission_rate,
      referral_l1_rate: commissionSettings.referral_l1_rate,
      referral_l2_rate: commissionSettings.referral_l2_rate
    });
    setShowCommissionDialog(true);
  };

  const handleUpdateCommissionSettings = async (e) => {
    e.preventDefault();
    
    try {
      await api.admin.updateCommissionSettings(commissionFormData);
      toast.success('Commission settings updated successfully!');
      
      setCommissionSettings(commissionFormData);
      setShowCommissionDialog(false);
      
    } catch (error) {
      console.error('Error updating commission settings:', error);
      toast.error('Failed to update commission settings');
    }
  };

  const handleEditFeeSettings = () => {
    setShowFeeDialog(true);
  };

  const handleUpdateFeeSettings = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const updatedSettings = {
      trading_fee_rate: parseFloat(formData.get('trading_fee_rate')) / 100,
      settlement_spread_rate: parseFloat(formData.get('settlement_spread_rate')) / 100,
      platform_fee_rate: parseFloat(formData.get('platform_fee_rate')) / 100,
      minimum_fee_amount: parseFloat(formData.get('minimum_fee_amount')),
      maximum_fee_amount: parseFloat(formData.get('maximum_fee_amount'))
    };

    try {
      await api.admin.updateFeeSettings(updatedSettings);
      toast.success('Fee settings updated successfully!');
      
      setFeeSettings(updatedSettings);
      setShowFeeDialog(false);
      
      // Refresh analytics if they were loaded
      if (feeAnalytics) {
        setAnalyticsLoading(true);
        try {
          const [feeRes, winLossRes] = await Promise.all([
            api.admin.getFeeAnalytics(30),
            api.admin.getWinLossAnalytics(30)
          ]);
          setFeeAnalytics(feeRes.data);
          setWinLossAnalytics(winLossRes.data);
        } catch (error) {
          console.error('Error refreshing analytics:', error);
        }
        setAnalyticsLoading(false);
      }
      
    } catch (error) {
      console.error('Error updating fee settings:', error);
      toast.error('Failed to update fee settings');
    }
  };

  // Check if user is admin
  if (!user || !user.roles || !user.roles.includes('admin')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950 flex items-center justify-center">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-6">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Admin Access Required</h2>
            <p className="text-gray-600 mb-4 dark:text-gray-400">You need admin privileges to access this page</p>
            <Link to="/">
              <Button>Back to Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Settings className="h-12 w-12 text-emerald-600 mx-auto animate-spin dark:text-emerald-400" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'text-green-600 bg-green-50';
      case 'closed': return 'text-yellow-600 bg-yellow-50';
      case 'settled': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Admin Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2 dark:text-gray-100">Admin Panel</h1>
            <p className="text-gray-600 dark:text-gray-400">Manage markets, settlements, and platform operations</p>
          </div>

          {/* Tabs — Tabs root drives TabsContent below; visible nav
              is replaced by a grouped sidebar on desktop and a native
              <select> on mobile. */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-6">
              {/* Desktop sidebar */}
              <aside className="hidden lg:block">
                <nav className="bg-white border border-gray-200 rounded-xl p-3 sticky top-20 dark:bg-slate-900/80 dark:border-slate-700">
                  {ADMIN_NAV_SECTIONS.map((section, sIdx) => (
                    <div key={section.label} className={sIdx > 0 ? 'mt-4' : ''}>
                      <div className="px-3 mb-1 text-[10px] uppercase tracking-wider text-gray-400 font-semibold dark:text-gray-500">
                        {section.label}
                      </div>
                      <div className="space-y-0.5">
                        {section.items.map((item) => {
                          const Icon = item.icon;
                          const isActive = activeTab === item.value;
                          return (
                            <button
                              key={item.value}
                              type="button"
                              onClick={() => setActiveTab(item.value)}
                              data-testid={`admin-tab-${item.value}`}
                              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                isActive
                                  ? 'bg-emerald-50 text-emerald-700'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }`}
                            >
                              <Icon className={`h-4 w-4 ${isActive ? 'text-emerald-600' : 'text-gray-400'}`} />
                              <span>{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </nav>
              </aside>

              {/* Mobile dropdown — grouped via <optgroup> so the user
                  still sees the section structure on a small screen. */}
              <div className="lg:hidden w-full mb-4">
                <div className="relative">
                  <select
                    value={activeTab}
                    onChange={(e) => setActiveTab(e.target.value)}
                    className="w-full p-3 border border-gray-200 rounded-lg bg-white shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none dark:bg-slate-900/80 dark:border-slate-700"
                    style={{
                      backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 1rem center',
                      backgroundSize: '1rem',
                    }}
                  >
                    {ADMIN_NAV_SECTIONS.map((section) => (
                      <optgroup key={section.label} label={section.label}>
                        {section.items.map((item) => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
              </div>

              {/* Content column wrapper — TabsContent blocks below
                  render here on desktop, full-width on mobile. */}
              <div className="min-w-0">
            
            <TabsContent value="dashboard" className="mt-6">
              {/* Dashboard Stats - Mobile Responsive */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
                <Card>
                  <CardContent className="pt-4 sm:pt-6 text-center">
                    <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 mx-auto mb-1 sm:mb-2 dark:text-blue-400" />
                    <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{dashboardData?.markets.total}</div>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Total Markets</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-4 sm:pt-6 text-center">
                    <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-green-600 mx-auto mb-1 sm:mb-2" />
                    <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{dashboardData?.markets.open}</div>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Open Markets</p>
                  </CardContent>
                </Card>
                
                <Card title="Summe aller Wett-Stakes (VC) — Engagement-KPI, kein Revenue">
                  <CardContent className="pt-4 sm:pt-6 text-center">
                    <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600 mx-auto mb-1 sm:mb-2 dark:text-purple-400" />
                    <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {dashboardData?.betting.total_volume?.toLocaleString()} VC
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Betting Handle</p>
                  </CardContent>
                </Card>

                <Card title="Summe aller an User ausgezahlten VC aus Settlements (Gewinne + Erstattungen)">
                  <CardContent className="pt-4 sm:pt-6 text-center">
                    <Receipt className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-600 mx-auto mb-1 sm:mb-2 dark:text-emerald-400" />
                    <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {dashboardData?.betting.total_payouts?.toLocaleString()} VC
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Auszahlungen gesamt</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4 sm:pt-6 text-center">
                    <Users className="h-6 w-6 sm:h-8 sm:w-8 text-orange-600 mx-auto mb-1 sm:mb-2" />
                    <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{dashboardData?.users.total}</div>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Total Users</p>
                  </CardContent>
                </Card>
              </div>

              {/* Platform Revenue — getrennt nach Currency. €-Revenue
                  aus Credit-Verkauf ist die echte Bilanz-Zahl;
                  VC-Revenue (Operator-Commission) ist im Multi-Tenant-
                  Modell später pro Tenant aufzuteilen. */}
              {platformRevenue && (
                <div className="grid grid-cols-2 gap-3 sm:gap-6 mb-6 sm:mb-8 max-w-2xl">
                  <Card className="border-l-4 border-l-emerald-500">
                    <CardContent className="pt-4 sm:pt-6">
                      <p className="text-xs text-gray-500 uppercase tracking-wide dark:text-gray-400">Platform Revenue (€)</p>
                      <div className="text-2xl sm:text-3xl font-bold text-emerald-600 mt-1 dark:text-emerald-400">
                        € {platformRevenue.eur_revenue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                        aus {platformRevenue.eur_transactions} Credit-Käufen
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="pt-4 sm:pt-6">
                      <p className="text-xs text-gray-500 uppercase tracking-wide dark:text-gray-400">Operator-Commission (VC)</p>
                      <div className="text-2xl sm:text-3xl font-bold text-blue-600 mt-1 dark:text-blue-400">
                        {platformRevenue.vc_revenue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} VC
                      </div>
                      <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                        aus {platformRevenue.vc_transactions} Provisions-Buchungen
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Advanced Charts Section - Mobile Responsive */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                {/* Betting Volume Trend */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <LineChart className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                      Betting Volume Trend (30 Days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {chartsLoading ? (
                      <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">Loading chart...</div>
                    ) : bettingTrends.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={bettingTrends}>
                          <defs>
                            <linearGradient id="volumeGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 11 }}
                            tickFormatter={(val) => val.slice(5)}
                          />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip 
                            contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                            formatter={(value, name) => [value.toLocaleString(), name === 'volume' ? 'Volume' : name === 'bets' ? 'Bets' : 'Unique Users']}
                          />
                          <Area type="monotone" dataKey="volume" stroke="#10b981" fill="url(#volumeGradient)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">No data available</div>
                    )}
                  </CardContent>
                </Card>

                {/* User Growth Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      User Growth (30 Days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {chartsLoading ? (
                      <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">Loading chart...</div>
                    ) : userGrowth.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <RechartsLine data={userGrowth}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 11 }}
                            tickFormatter={(val) => val.slice(5)}
                          />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip 
                            contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                            formatter={(value, name) => [value, name === 'cumulative' ? 'Total Users' : 'New Users']}
                          />
                          <Line type="monotone" dataKey="cumulative" stroke="#3b82f6" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="newUsers" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                          <Legend />
                        </RechartsLine>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">No data available</div>
                    )}
                  </CardContent>
                </Card>

                {/* Category Distribution Pie Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      Volume by Category
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {chartsLoading ? (
                      <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">Loading chart...</div>
                    ) : categoryDistribution.length > 0 ? (
                      <ResponsiveContainer width="100%" height={320}>
                        <RechartsPie>
                          <Pie
                            data={categoryDistribution.filter(d => (d.volume || 0) > 0)}
                            cx="50%"
                            cy="48%"
                            innerRadius={55}
                            outerRadius={95}
                            paddingAngle={2}
                            dataKey="volume"
                            nameKey="name"
                            // Nur Slices >= 5% direkt beschriften — sonst
                            // ueberlappen sich die Labels bei vielen
                            // Mini-Slices (Civic-Kategorien + Sports
                            // gemischt). Kleinere Kategorien stehen
                            // unten in der Legend.
                            label={({ name, percent }) =>
                              percent >= 0.05 ? `${name} (${(percent * 100).toFixed(0)}%)` : ''
                            }
                            labelLine={false}
                          >
                            {categoryDistribution.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                            formatter={(value) => [value.toLocaleString() + ' credits', 'Volume']}
                          />
                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            iconSize={10}
                            wrapperStyle={{ fontSize: '12px' }}
                            formatter={(value, entry) => {
                              const total = categoryDistribution.reduce((s, d) => s + (d.volume || 0), 0) || 1;
                              const pct = ((entry.payload.volume || 0) / total) * 100;
                              return `${value} (${pct.toFixed(0)}%)`;
                            }}
                          />
                        </RechartsPie>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">No data available</div>
                    )}
                  </CardContent>
                </Card>

                {/* Top Markets Bar Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5 text-orange-600" />
                      Top Markets by Volume
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {chartsLoading ? (
                      <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">Loading chart...</div>
                    ) : topMarketsChart.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={topMarketsChart} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis 
                            dataKey="title" 
                            type="category" 
                            width={120}
                            tick={{ fontSize: 10 }}
                          />
                          <Tooltip 
                            contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                            formatter={(value) => [value.toLocaleString() + ' credits', 'Volume']}
                          />
                          <Bar dataKey="volume" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">No data available</div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Market Status Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Market Status Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-green-50 rounded-lg dark:bg-emerald-500/10">
                      <div className="text-2xl font-bold text-green-600">{dashboardData?.markets.open}</div>
                      <p className="text-green-700">Open Markets</p>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg dark:bg-amber-500/10">
                      <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{dashboardData?.markets.closed}</div>
                      <p className="text-yellow-700">Closed Markets</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg dark:bg-blue-500/10">
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{dashboardData?.markets.settled}</div>
                      <p className="text-blue-700 dark:text-blue-300">Settled Markets</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

            </TabsContent>

            {/* Notifications — own sidebar entry under Dashboard. */}
            <TabsContent value="notifications" className="mt-6 space-y-6">
              <AdminNotificationsCard />
              <PlatformNotificationsCard />
            </TabsContent>

            {/* Credit Sales (€) — Übersicht aller Real-Money-Käufe. */}
            <TabsContent value="credit-sales" className="mt-6">
              <CreditSalesCard />
            </TabsContent>

            <TabsContent value="activity-logs" className="mt-6">
              <ActivityLogsCard />
            </TabsContent>

            <TabsContent value="sentry-issues" className="mt-6">
              <SentryIssuesCard />
            </TabsContent>

            {/* Markets Overview Tab */}
            <TabsContent value="markets" className="mt-6">
              <div className="space-y-6">
                {/* Market Statistics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="border-l-4 border-l-emerald-500">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Open Markets</p>
                          <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                            {markets.filter(m => m.status === 'open').length}
                          </p>
                        </div>
                        <Play className="h-8 w-8 text-emerald-500" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-yellow-500">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Closed (Pending)</p>
                          <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                            {markets.filter(m => m.status === 'closed').length}
                          </p>
                        </div>
                        <Clock className="h-8 w-8 text-yellow-500" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Settled</p>
                          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            {markets.filter(m => m.status === 'settled').length}
                          </p>
                        </div>
                        <CheckCircle className="h-8 w-8 text-blue-500" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-gray-500">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Markets</p>
                          <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                            {markets.length}
                          </p>
                        </div>
                        <BarChart3 className="h-8 w-8 text-gray-500 dark:text-gray-400" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Markets Table — wrapped in an IIFE so we can compute
                    the filtered list once and reuse it inside the table. */}
                {(() => {
                  const distinctCats = Array.from(new Set(markets.map(m => m.category).filter(Boolean))).sort();
                  const filteredMarkets = markets.filter(m => {
                    if (marketStatusFilter !== 'all' && m.status !== marketStatusFilter) return false;
                    if (marketCategoryFilter !== 'all' && (m.category || '').toLowerCase() !== marketCategoryFilter.toLowerCase()) return false;
                    if (marketTypeFilter !== 'all' && (m.mode || 'prediction') !== marketTypeFilter) return false;
                    if (marketBoostFilter === 'boosted' && !isMarketBoosted(m)) return false;
                    if (marketSearch && !((m.title || '').toLowerCase().includes(marketSearch.toLowerCase()))) return false;
                    return true;
                  });
                  if (marketCloseSort) {
                    const dir = marketCloseSort === 'asc' ? 1 : -1;
                    filteredMarkets.sort((a, b) => {
                      const ta = a.closes_at ? new Date(a.closes_at).getTime() : Infinity;
                      const tb = b.closes_at ? new Date(b.closes_at).getTime() : Infinity;
                      return (ta - tb) * dir;
                    });
                  }
                  return (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5" />
                      All Markets
                    </CardTitle>
                    <CardDescription>
                      View, manage, and control all markets. Close open markets or settle closed ones.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-3 mb-4 pb-3 border-b border-gray-100 dark:border-slate-800">
                      <input
                        type="text"
                        placeholder="Markt suchen…"
                        value={marketSearch}
                        onChange={(e) => setMarketSearch(e.target.value)}
                        className="flex-1 min-w-[180px] px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 dark:border-slate-700"
                      />
                      <select
                        value={marketStatusFilter}
                        onChange={(e) => setMarketStatusFilter(e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-200 rounded-md dark:border-slate-700"
                      >
                        <option value="all">Alle Status</option>
                        <option value="open">Open</option>
                        <option value="closed">Closed</option>
                        <option value="settled">Settled</option>
                      </select>
                      <select
                        value={marketCategoryFilter}
                        onChange={(e) => setMarketCategoryFilter(e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-200 rounded-md dark:border-slate-700"
                      >
                        <option value="all">Alle Kategorien</option>
                        {distinctCats.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <select
                        value={marketTypeFilter}
                        onChange={(e) => setMarketTypeFilter(e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-200 rounded-md dark:border-slate-700"
                      >
                        <option value="all">Alle Typen</option>
                        <option value="prediction">Prediction</option>
                        <option value="civic">Civic</option>
                      </select>
                      <select
                        value={marketBoostFilter}
                        onChange={(e) => setMarketBoostFilter(e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-200 rounded-md dark:border-slate-700"
                      >
                        <option value="all">Boost: alle</option>
                        <option value="boosted">Nur geboostet</option>
                      </select>
                      <span className="text-xs text-gray-500 self-center dark:text-gray-400">
                        {filteredMarkets.length} / {markets.length}
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50 dark:bg-slate-800/60">
                            <th className="text-left p-3 font-medium text-gray-600 dark:text-gray-400">Market</th>
                            <th className="text-left p-3 font-medium text-gray-600 dark:text-gray-400">Type</th>
                            <th className="text-left p-3 font-medium text-gray-600 dark:text-gray-400">Category</th>
                            <th className="text-left p-3 font-medium text-gray-600 dark:text-gray-400">Status</th>
                            <th
                              className="text-left p-3 font-medium text-gray-600 cursor-pointer select-none hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                              onClick={() => setMarketCloseSort(s => s === null ? 'asc' : s === 'asc' ? 'desc' : null)}
                              title="Sortieren nach Schließdatum"
                            >
                              Closes At {marketCloseSort === 'asc' ? '▲' : marketCloseSort === 'desc' ? '▼' : '↕'}
                            </th>
                            <th className="text-left p-3 font-medium text-gray-600 dark:text-gray-400">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredMarkets.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="p-8 text-center text-gray-500 dark:text-gray-400">
                                {markets.length === 0 ? 'No markets found. Create your first market!' : 'Keine Treffer mit aktuellem Filter.'}
                              </td>
                            </tr>
                          ) : (
                            filteredMarkets.map((market) => (
                              <tr key={market.id} className="border-b hover:bg-gray-50 dark:hover:bg-slate-800/60">
                                <td className="p-3">
                                  <div>
                                    <p className="font-medium text-gray-900 truncate max-w-xs dark:text-gray-100">{market.title}</p>
                                    <p className="text-xs text-gray-500 truncate max-w-xs dark:text-gray-400">{market.description?.substring(0, 50)}...</p>
                                  </div>
                                </td>
                                <td className="p-3">
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                                    (market.mode || 'prediction') === 'civic'
                                      ? 'bg-purple-100 text-purple-700'
                                      : 'bg-sky-100 text-sky-700'
                                  }`}>
                                    {(market.mode || 'prediction') === 'civic' ? 'Civic' : 'Prediction'}
                                  </span>
                                </td>
                                <td className="p-3">
                                  <span className="px-2 py-1 bg-gray-100 rounded-full text-xs dark:bg-slate-800">
                                    {market.category || market.civic_category || '—'}
                                  </span>
                                </td>
                                <td className="p-3">
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                                    market.status === 'open'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : market.status === 'closed'
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {market.status === 'open' ? 'Open' : market.status === 'closed' ? 'Closed' : 'Settled'}
                                  </span>
                                </td>
                                <td className="p-3 text-gray-600 text-xs whitespace-nowrap dark:text-gray-400">
                                  {market.closes_at ? new Date(market.closes_at).toLocaleString('de-DE', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: false,
                                  }) : 'N/A'}
                                </td>
                                <td className="p-3">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Aktionen">
                                        <MoreVertical className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-48">
                                      {market.status === 'open' && (
                                        <DropdownMenuItem onClick={() => handleCloseMarket(market.id)}>
                                          <XCircle className="h-4 w-4 mr-2 text-yellow-600" /> Close
                                        </DropdownMenuItem>
                                      )}
                                      {market.status === 'closed' && (
                                        <>
                                          <DropdownMenuItem onClick={() => handleReopenMarket(market.id)}>
                                            <Play className="h-4 w-4 mr-2 text-emerald-600" /> Reopen
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => {
                                            setSettlementData({ market_id: market.id, winning_outcome_id: '' });
                                            setActiveTab('settle');
                                          }}>
                                            <CheckCircle className="h-4 w-4 mr-2 text-blue-600" /> Settle
                                          </DropdownMenuItem>
                                        </>
                                      )}
                                      {market.status !== 'settled' && (
                                        isMarketBoosted(market) ? (
                                          <DropdownMenuItem onClick={() => handleToggleBoost(market.id, false)}>
                                            <Zap className="h-4 w-4 mr-2 text-orange-500" fill="currentColor" /> Boost entfernen
                                          </DropdownMenuItem>
                                        ) : (
                                          <DropdownMenuItem onClick={() => handleToggleBoost(market.id, true)}>
                                            <Zap className="h-4 w-4 mr-2 text-orange-500" fill="currentColor" /> Boosten (7 Tage)
                                          </DropdownMenuItem>
                                        )
                                      )}
                                      <DropdownMenuItem onClick={() => {
                                        setEditMarket(market);
                                        setEditMarketData({
                                          title: market.title,
                                          description: market.description,
                                          category: market.category,
                                          closes_at: market.closes_at ? new Date(market.closes_at).toISOString().slice(0, 16) : ''
                                        });
                                        setShowEditDialog(true);
                                      }}>
                                        <Pencil className="h-4 w-4 mr-2" /> Bearbeiten
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
                  );
                })()}
              </div>
            </TabsContent>

            <TabsContent value="users" className="mt-6">
              {/* User Analytics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <Card>
                  <CardContent className="pt-6 text-center">
                    <Users className="h-8 w-8 text-blue-600 mx-auto mb-2 dark:text-blue-400" />
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{userAnalytics?.overview.total_users || 0}</div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Users</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6 text-center">
                    <Settings className="h-8 w-8 text-purple-600 mx-auto mb-2 dark:text-purple-400" />
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{userAnalytics?.overview.admin_users || 0}</div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Admins</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6 text-center">
                    <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{userAnalytics?.overview.active_users || 0}</div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Active Users</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6 text-center">
                    <TrendingUp className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{userAnalytics?.overview.recent_registrations || 0}</div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">New (30 days)</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* User Management */}
                <div className="lg:col-span-2">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div>
                        <CardTitle>User Management</CardTitle>
                        <CardDescription>Manage user accounts and permissions</CardDescription>
                      </div>
                      <Button onClick={handleOpenCreateUserDialog}>
                        <Plus className="h-4 w-4 mr-2" />
                        Create User
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {/* Search and Filter */}
                      <div className="flex gap-4 mb-4">
                        <Input
                          placeholder="Search users..."
                          value={userSearchTerm}
                          onChange={(e) => setUserSearchTerm(e.target.value)}
                          className="flex-1"
                        />
                        <select
                          className="px-3 py-2 border rounded-md"
                          value={userRoleFilter}
                          onChange={(e) => setUserRoleFilter(e.target.value)}
                        >
                          <option value="">All Roles</option>
                          <option value="user">Users</option>
                          <option value="admin">Admins</option>
                          <option value="creator">Creators</option>
                        </select>
                      </div>

                      {/* Users Table */}
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {filteredUsers.map((user) => (
                          user && user.handle && user.email && user.roles ? (
                          <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/60">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                                <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400">
                                  {user.handle.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium">{user.handle}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{user.email}</p>
                                <div className="flex gap-1 mt-1">
                                  {user.roles.map(role => (
                                    <Badge key={role} variant={role === 'admin' ? 'default' : 'secondary'} className="text-xs">
                                      {role}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewUserDetails(user.id)}
                              >
                                View
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditUser(user)}
                              >
                                Edit
                              </Button>
                            </div>
                          </div>
                          ) : null
                        ))}
                        
                        {filteredUsers.length === 0 && (
                          <div className="text-center py-8">
                            <p className="text-gray-500 dark:text-gray-400">No users found</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Top Bettors */}
                <Card>
                  <CardHeader>
                    <CardTitle>Top Bettors</CardTitle>
                    <CardDescription>Users with highest betting volume</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {userAnalytics?.top_bettors?.slice(0, 5).map((bettor, index) => (
                        <div key={bettor.user_id} className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                              <span className="text-xs font-bold text-emerald-700 dark:text-emerald-400">#{index + 1}</span>
                            </div>
                            <div>
                              <p className="font-medium text-sm">{bettor.handle}</p>
                              <p className="text-xs text-gray-600 dark:text-gray-400">{bettor.bet_count} bets</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-sm">{bettor.total_wagered.toLocaleString()}</p>
                            <p className="text-xs text-gray-600 dark:text-gray-400">credits</p>
                          </div>
                        </div>
                      )) || []}
                      
                      {(!userAnalytics?.top_bettors || userAnalytics.top_bettors.length === 0) && (
                        <div className="text-center py-4">
                          <p className="text-gray-500 text-sm dark:text-gray-400">No betting activity yet</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="categories" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Create Category Form */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Plus className="h-5 w-5" />
                      Create New Category
                    </CardTitle>
                    <CardDescription>
                      Add a new category for organizing markets
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleCreateCategory} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="cat_name">Category Name</Label>
                        <Input
                          id="cat_name"
                          placeholder="e.g., Science"
                          value={newCategory.name}
                          onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="cat_description">Description</Label>
                        <Input
                          id="cat_description"
                          placeholder="Category description"
                          value={newCategory.description}
                          onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="cat_color">Color</Label>
                          <Input
                            id="cat_color"
                            type="color"
                            value={newCategory.color}
                            onChange={(e) => setNewCategory({...newCategory, color: e.target.value})}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="cat_icon">Icon</Label>
                          <select
                            id="cat_icon"
                            className="w-full p-2 border rounded-md"
                            value={newCategory.icon}
                            onChange={(e) => setNewCategory({...newCategory, icon: e.target.value})}
                          >
                            <option value="Trophy">Trophy</option>
                            <option value="Vote">Vote</option>
                            <option value="DollarSign">DollarSign</option>
                            <option value="Users">Users</option>
                            <option value="Target">Target</option>
                            <option value="TrendingUp">TrendingUp</option>
                            <option value="BarChart3">BarChart3</option>
                          </select>
                        </div>
                      </div>
                      
                      <Button type="submit" className="w-full">
                        <Plus className="h-4 w-4 mr-2" />
                        Create Category
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* Categories List */}
                <Card>
                  <CardHeader>
                    <CardTitle>Existing Categories</CardTitle>
                    <CardDescription>Manage your market categories</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {categories.map((category) => (
                        <div key={category.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: category.color }}
                            ></div>
                            <div>
                              <p className="font-medium">{category.name}</p>
                              {category.description && (
                                <p className="text-sm text-gray-600 dark:text-gray-400">{category.description}</p>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteCategory(category.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      ))}
                      
                      {categories.length === 0 && (
                        <div className="text-center py-8">
                          <p className="text-gray-500 dark:text-gray-400">No categories yet</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Virtual Shop Items — what the user-facing /shop page lists. */}
            <TabsContent value="virtual-shop" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ShoppingCart className="h-5 w-5" />
                      {editingVirtualItemId ? 'Virtuelles Item bearbeiten' : 'Neues virtuelles Item'}
                    </CardTitle>
                    <CardDescription>
                      Cosmetic-Items wie Avatar-Emoji, Profilrahmen, Username-Farbe — werden im User-Shop angezeigt.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmitVirtualShopItem} className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="vshop_id">ID (Slug)</Label>
                          <Input
                            id="vshop_id"
                            placeholder="avatar-emoji"
                            value={virtualShopForm.id}
                            onChange={(e) => setVirtualShopForm({ ...virtualShopForm, id: e.target.value })}
                            disabled={!!editingVirtualItemId}
                            required
                          />
                          <p className="text-[10px] text-gray-400 dark:text-gray-500">URL-safe, eindeutig. Beim Bearbeiten gesperrt.</p>
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="vshop_name">Name</Label>
                          <Input
                            id="vshop_name"
                            placeholder="Avatar-Emoji"
                            value={virtualShopForm.name}
                            onChange={(e) => setVirtualShopForm({ ...virtualShopForm, name: e.target.value })}
                            required
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="vshop_desc">Beschreibung</Label>
                        <Input
                          id="vshop_desc"
                          placeholder="Wähle ein Emoji als Profilbild"
                          value={virtualShopForm.description}
                          onChange={(e) => setVirtualShopForm({ ...virtualShopForm, description: e.target.value })}
                          required
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="vshop_price">Preis (Credits)</Label>
                          <Input
                            id="vshop_price"
                            type="number"
                            min={0}
                            value={virtualShopForm.price}
                            onChange={(e) => setVirtualShopForm({ ...virtualShopForm, price: parseInt(e.target.value, 10) || 0 })}
                            required
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="vshop_category">Kategorie</Label>
                          <select
                            id="vshop_category"
                            value={virtualShopForm.category}
                            onChange={(e) => setVirtualShopForm({ ...virtualShopForm, category: e.target.value })}
                            className="w-full p-2 border border-gray-200 rounded-md text-sm dark:border-slate-700"
                          >
                            <option value="avatar">avatar</option>
                            <option value="frame">frame</option>
                            <option value="title">title</option>
                            <option value="color">color</option>
                            <option value="boost">boost</option>
                            <option value="stat">stat</option>
                          </select>
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="vshop_tier">Tier</Label>
                          <select
                            id="vshop_tier"
                            value={virtualShopForm.tier || ''}
                            onChange={(e) => setVirtualShopForm({ ...virtualShopForm, tier: e.target.value })}
                            className="w-full p-2 border border-gray-200 rounded-md text-sm dark:border-slate-700"
                          >
                            <option value="bronze">bronze</option>
                            <option value="silver">silver</option>
                            <option value="gold">gold</option>
                          </select>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="vshop_effect">Effect (interner Key)</Label>
                        <Input
                          id="vshop_effect"
                          placeholder="avatar_emoji"
                          value={virtualShopForm.effect || ''}
                          onChange={(e) => setVirtualShopForm({ ...virtualShopForm, effect: e.target.value })}
                        />
                        <p className="text-[10px] text-gray-400 dark:text-gray-500">Optional. Steuert die Spezial-Logik (z.B. profile_frame, name_color).</p>
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="vshop_image_url">Hintergrundbild</Label>
                        <Input
                          id="vshop_image_url"
                          placeholder="https://… oder data:image/…"
                          value={virtualShopForm.image_url || ''}
                          onChange={(e) => setVirtualShopForm({ ...virtualShopForm, image_url: e.target.value })}
                        />
                        <div className="flex items-center gap-3 mt-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleVirtualShopImageFile(e.target.files?.[0])}
                            className="text-xs text-gray-500 dark:text-gray-400"
                          />
                          {virtualShopForm.image_url && (
                            <button
                              type="button"
                              onClick={() => setVirtualShopForm({ ...virtualShopForm, image_url: '' })}
                              className="text-xs text-rose-600 hover:underline"
                            >
                              Entfernen
                            </button>
                          )}
                        </div>
                        {virtualShopForm.image_url && (
                          <div className="mt-2 flex items-center gap-3">
                            <img
                              src={virtualShopForm.image_url}
                              alt="Vorschau"
                              className="w-16 h-16 object-cover rounded-lg border border-gray-200 dark:border-slate-700"
                            />
                            <span className="text-[10px] text-gray-400 truncate dark:text-gray-500">
                              {virtualShopForm.image_url.startsWith('data:') ? 'inline (data URI)' : virtualShopForm.image_url.slice(0, 60)}
                            </span>
                          </div>
                        )}
                        <p className="text-[10px] text-gray-400 dark:text-gray-500">Optional. URL eintragen oder Datei {'<'} 1 MB hochladen (wird als data: URI gespeichert).</p>
                      </div>

                      <div className="flex gap-2">
                        <Button type="submit" className="flex-1">
                          <Plus className="h-4 w-4 mr-2" />
                          {editingVirtualItemId ? 'Speichern' : 'Anlegen'}
                        </Button>
                        {editingVirtualItemId && (
                          <Button type="button" variant="outline" onClick={resetVirtualShopForm}>
                            Abbrechen
                          </Button>
                        )}
                      </div>
                    </form>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Virtuelle Items ({virtualShopItems.length})</CardTitle>
                    <CardDescription>Alles, was im User-Shop sichtbar ist.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-[480px] overflow-y-auto">
                      {virtualShopItems.length === 0 && (
                        <p className="text-center text-gray-500 py-6 dark:text-gray-400">Noch keine virtuellen Items</p>
                      )}
                      {virtualShopItems.map((item) => (
                        <div
                          key={item.id}
                          className={`flex items-center justify-between p-3 border rounded-lg ${editingVirtualItemId === item.id ? 'border-emerald-400 bg-emerald-50' : ''}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium truncate">{item.name}</p>
                              {item.tier && (
                                <Badge variant="outline" className="text-[10px]">{item.tier}</Badge>
                              )}
                              <Badge variant="secondary" className="text-[10px]">{item.category}</Badge>
                            </div>
                            <p className="text-xs text-gray-500 truncate dark:text-gray-400">{item.description}</p>
                            <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
                              <span className="font-mono">{item.id}</span> · {item.price} Credits
                              {item.effect ? ` · effect:${item.effect}` : ''}
                            </p>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button size="sm" variant="outline" onClick={() => handleEditVirtualShopItem(item)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteVirtualShopItem(item.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="packages" className="mt-6">
              <div className="space-y-6">
                {/* Header with Create Button */}
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Credit Packages</h2>
                    <p className="text-gray-600 dark:text-gray-400">Manage credit packages with pricing and discounts</p>
                  </div>
                  <Button onClick={() => handleOpenPackageDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Package
                  </Button>
                </div>

                {/* Packages List */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {creditPackages.map((pkg) => (
                    <Card key={pkg.id} className={`relative ${pkg.popular ? 'border-emerald-500 border-2' : ''} ${!pkg.active ? 'opacity-60' : ''}`}>
                      {pkg.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <Badge className="bg-emerald-500 text-white">Popular</Badge>
                        </div>
                      )}
                      
                      <CardContent className="pt-6">
                        <div className="text-center mb-4">
                          <div className="mb-2">
                            <Zap className={`h-8 w-8 mx-auto ${pkg.popular ? 'text-emerald-500' : 'text-gray-400'}`} />
                          </div>
                          <h3 className="font-bold text-lg">{pkg.name}</h3>
                        </div>
                        
                        <div className="text-center mb-4">
                          <div className="text-3xl font-bold text-emerald-600 mb-1 dark:text-emerald-400">
                            {pkg.total_credits}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {pkg.credits} + {pkg.bonus_credits} bonus credits
                          </div>
                        </div>
                        
                        <div className="text-center mb-4">
                          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                            €{pkg.price.toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            €{pkg.price_per_credit.toFixed(3)} per credit
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-center space-x-2 mb-4">
                          <Badge variant={pkg.active ? 'default' : 'secondary'}>
                            {pkg.active ? 'Active' : 'Inactive'}
                          </Badge>
                          <Badge variant="outline">
                            Order: {pkg.display_order}
                          </Badge>
                        </div>
                        
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenPackageDialog(pkg)}
                            className="flex-1"
                          >
                            <Settings className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeletePackage(pkg.id)}
                          >
                            <XCircle className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {creditPackages.length === 0 && (
                    <div className="col-span-full text-center py-12">
                      <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4 dark:text-gray-500" />
                      <p className="text-gray-500 mb-4 dark:text-gray-400">No credit packages yet</p>
                      <Button onClick={() => handleOpenPackageDialog()}>
                        Create Your First Package
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="create" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="h-5 w-5" />
                    Create New Market
                  </CardTitle>
                  <CardDescription>
                    Create a new prediction market with YES/NO outcomes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateMarket} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Market Title</Label>
                      <Input
                        id="title"
                        placeholder="e.g., Will Bitcoin reach $100,000 in 2024?"
                        value={newMarket.title}
                        onChange={(e) => setNewMarket({...newMarket, title: e.target.value})}
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Input
                        id="description"
                        placeholder="Market description and resolution criteria"
                        value={newMarket.description}
                        onChange={(e) => setNewMarket({...newMarket, description: e.target.value})}
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Markt-Typ</Label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setNewMarket({...newMarket, mode: 'prediction'})}
                          className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                            newMarket.mode === 'prediction'
                              ? 'bg-sky-600 text-white border-sky-600'
                              : 'bg-white text-sky-700 border-sky-200 hover:bg-sky-50'
                          }`}
                        >
                          Prediction
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewMarket({...newMarket, mode: 'civic'})}
                          className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                            newMarket.mode === 'civic'
                              ? 'bg-purple-600 text-white border-purple-600'
                              : 'bg-white text-purple-700 border-purple-200 hover:bg-purple-50'
                          }`}
                        >
                          Civic
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {newMarket.mode === 'civic'
                          ? 'Civic: kostenlose Abstimmung mit JA/NEIN, keine Wetten/Credits, automatisches Settle bei closes_at.'
                          : 'Prediction: parimutuel-Wetten mit YES/NO, manuelles Settle durch Admin.'}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="category">{newMarket.mode === 'civic' ? 'Civic-Kategorie' : 'Category'}</Label>
                        {newMarket.mode === 'civic' ? (
                          <select
                            id="category"
                            className="w-full p-2 border rounded-md"
                            value={newMarket.civic_category}
                            onChange={(e) => setNewMarket({...newMarket, civic_category: e.target.value})}
                            required
                          >
                            <option value="">Civic-Kategorie wählen…</option>
                            {CIVIC_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        ) : (
                          <select
                            id="category"
                            className="w-full p-2 border rounded-md"
                            value={newMarket.category}
                            onChange={(e) => setNewMarket({...newMarket, category: e.target.value})}
                            required
                          >
                            <option value="">Select a category...</option>
                            {categories.map((category) => (
                              <option key={category.id} value={category.name}>
                                {category.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="closes_at">Closing Date</Label>
                        <Input
                          id="closes_at"
                          type="datetime-local"
                          value={newMarket.closes_at}
                          onChange={(e) => setNewMarket({...newMarket, closes_at: e.target.value})}
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="image_url">Cover-Bild (optional)</Label>
                      <ImageUploadField
                        value={newMarket.image_url}
                        onChange={(url) => setNewMarket({...newMarket, image_url: url})}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Wird oben auf der Markt-Karte angezeigt. Leer lassen für textbasierte Karte.
                      </p>
                    </div>

                    <Button type="submit" className="w-full">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Market
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="manage" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Manage Markets</CardTitle>
                  <CardDescription>Update market status and monitor activity</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {markets.map((market) => (
                      <div key={market.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 dark:text-gray-100">{market.title}</h3>
                            <p className="text-sm text-gray-600 mt-1 dark:text-gray-400">{market.description}</p>
                            <div className="flex items-center gap-4 mt-2">
                              <Badge variant="secondary">{market.category}</Badge>
                              <Badge className={getStatusColor(market.status)} variant="secondary">
                                {market.status}
                              </Badge>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Closes: {new Date(market.closes_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex flex-col gap-2 ml-4">
                            {market.status === 'open' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateMarketStatus(market.id, 'closed')}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Close
                              </Button>
                            )}
                            
                            {market.status === 'closed' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateMarketStatus(market.id, 'open')}
                              >
                                Reopen
                              </Button>
                            )}
                            
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenEditMarket(market)}
                            >
                              <Settings className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteMarket(market.id, market.title)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="settle" className="mt-6 space-y-6">
              {/* Bulk-Settle Card — pick winners for many closed
                  markets at once and settle them in one round-trip
                  via /admin/markets/bulk-settle. */}
              <BulkSettleCard
                markets={markets}
                marketOutcomes={marketOutcomes}
                onSettled={fetchAdminData}
              />

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    Settle Single Market
                  </CardTitle>
                  <CardDescription>
                    Resolve a single market with detailed outcome picker
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSettleMarket} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="market_select">Select Market to Settle</Label>
                      <select
                        id="market_select"
                        className="w-full p-2 border rounded-md"
                        value={settlementData.market_id}
                        onChange={(e) => setSettlementData({...settlementData, market_id: e.target.value})}
                        required
                      >
                        <option value="">Choose a closed market...</option>
                        {markets
                          .filter(market => market.status === 'closed')
                          .map(market => (
                            <option key={market.id} value={market.id}>
                              {market.title}
                            </option>
                          ))}
                      </select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="outcome_select">Winning Outcome</Label>
                      <select
                        id="outcome_select"
                        className="w-full p-3 border-2 rounded-lg text-base bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:bg-slate-900/80"
                        value={settlementData.winning_outcome_id}
                        onChange={(e) => setSettlementData({...settlementData, winning_outcome_id: e.target.value})}
                        required
                      >
                        <option value="" className="text-gray-500 dark:text-gray-400">Select the winning outcome...</option>
                        {settlementData.market_id && marketOutcomes[settlementData.market_id] && 
                          marketOutcomes[settlementData.market_id].map(outcome => {
                            // For YES/NO markets, display user-friendly labels
                            let displayLabel = outcome.label;
                            let isYes = false;
                            let isNo = false;
                            
                            if (outcome.label === 'Yes' || outcome.label === 'YES' || outcome.label.toLowerCase() === 'yes') {
                              displayLabel = '✅ YES - The event occurred / prediction is true';
                              isYes = true;
                            } else if (outcome.label === 'No' || outcome.label === 'NO' || outcome.label.toLowerCase() === 'no') {
                              displayLabel = '❌ NO - The event did not occur / prediction is false';
                              isNo = true;
                            } else {
                              // For any other outcome labels, keep original but make it clear
                              displayLabel = `📋 ${outcome.label}`;
                            }
                            
                            return (
                              <option 
                                key={outcome.id} 
                                value={outcome.id}
                                className={
                                  isYes ? 'text-green-700 font-semibold' : 
                                  isNo ? 'text-red-700 font-semibold' : 
                                  'text-gray-700'
                                }
                              >
                                {displayLabel}
                              </option>
                            );
                          })
                        }
                      </select>
                      {settlementData.winning_outcome_id && (
                        <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-amber-500/10">
                          <p className="text-sm text-yellow-800">
                            <strong>⚠️ Important:</strong> This selection will determine which users win their bets. 
                            Make sure you've verified the market outcome before proceeding.
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Settlement is irreversible. Ensure the market outcome is correct before proceeding.
                      </AlertDescription>
                    </Alert>
                    
                    <Button type="submit" className="w-full">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Settle Market
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Auszahlungen — eigener Sidebar-Tab: wer hat beim Settlen
                wieviel VC bekommen (GET /admin/markets/{id}/payouts). */}
            <TabsContent value="payouts" className="mt-6 space-y-6">
              <SettlementPayoutsCard markets={markets} />
            </TabsContent>

            {/* Active Bets Management Tab */}
            <TabsContent value="bets" className="mt-6">
              <div className="space-y-6">
                {/* Betting Statistics Cards */}
                {bettingStats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Active Bets</CardTitle>
                        <Target className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{bettingStats.active_bets}</div>
                        <p className="text-xs text-muted-foreground">
                          of {bettingStats.total_bets} total bets
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Active Volume</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{bettingStats.active_volume.toLocaleString()}</div>
                        <p className="text-xs text-muted-foreground">
                          Credits at risk
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Volume</CardTitle>
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{bettingStats.total_volume.toLocaleString()}</div>
                        <p className="text-xs text-muted-foreground">
                          All-time betting volume
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Recent Activity</CardTitle>
                        <Clock className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{bettingStats.recent_bets_24h}</div>
                        <p className="text-xs text-muted-foreground">
                          Bets in last 24h
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Active Bets Table */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5" />
                      All Active Bets
                    </CardTitle>
                    <CardDescription>
                      Overview of all active betting positions in the system
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {activeBets.length === 0 ? (
                      <div className="text-center py-8">
                        <Target className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">No active bets found</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-gray-50 dark:bg-slate-800/60">
                              <th className="p-3 text-left font-medium">User</th>
                              <th className="p-3 text-left font-medium">Market</th>
                              <th className="p-3 text-left font-medium">Position</th>
                              <th className="p-3 text-left font-medium">Stake</th>
                              <th className="p-3 text-left font-medium">Potential Payout</th>
                              <th className="p-3 text-left font-medium">Created</th>
                              <th className="p-3 text-left font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeBets.map((bet) => (
                              <tr key={bet.id} className="border-b hover:bg-gray-50 dark:hover:bg-slate-800/60">
                                <td className="p-3">
                                  <div>
                                    <div className="font-medium">{bet.user?.handle || 'Unknown'}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">{bet.user?.email}</div>
                                  </div>
                                </td>
                                <td className="p-3">
                                  <div>
                                    <div className="font-medium">{bet.legs[0]?.market?.title || 'Unknown Market'}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      <Badge variant="outline" className="text-xs">
                                        {bet.legs[0]?.market?.category}
                                      </Badge>
                                    </div>
                                  </div>
                                </td>
                                <td className="p-3">
                                  <Badge variant={bet.legs[0]?.betting_yes ? 'default' : 'destructive'}>
                                    {bet.legs[0]?.betting_yes ? 'YES' : 'NO'}
                                  </Badge>
                                </td>
                                <td className="p-3 font-medium">{bet.stake.toLocaleString()} Credits</td>
                                <td className="p-3 font-medium text-green-600">
                                  {bet.potential_payout.toLocaleString()} Credits
                                </td>
                                <td className="p-3 text-gray-600 dark:text-gray-400">
                                  {bet.placed_at ? new Date(bet.placed_at).toLocaleDateString() : '—'}
                                </td>
                                <td className="p-3">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedBet(bet);
                                      setShowBetDetailsDialog(true);
                                    }}
                                  >
                                    View Details
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Top Markets by Betting Activity */}
                {bettingStats?.top_markets && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Trophy className="h-5 w-5" />
                        Top Markets by Activity
                      </CardTitle>
                      <CardDescription>
                        Markets with the most betting activity
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {bettingStats.top_markets.map((market, index) => (
                          <div key={market.market_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg dark:bg-slate-800/60">
                            <div className="flex items-center gap-3">
                              <div className="flex items-center justify-center w-8 h-8 bg-emerald-100 text-emerald-700 rounded-full font-bold dark:text-emerald-400">
                                {index + 1}
                              </div>
                              <div>
                                <div className="font-medium">{market.market_title}</div>
                                <div className="text-sm text-gray-600 dark:text-gray-400">{market.bet_count} bets</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold">{market.total_stake.toLocaleString()} Credits</div>
                              <div className="text-sm text-gray-600 dark:text-gray-400">Total Volume</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Commission Settings Tab */}
            <TabsContent value="commissions" className="mt-6">
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <DollarSign className="h-5 w-5" />
                          Commission Settings
                        </CardTitle>
                        <CardDescription>
                          Configure creator and referral commission rates
                        </CardDescription>
                      </div>
                      <Button onClick={handleEditCommissionSettings}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Settings
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <Card className="border-2 border-emerald-200 dark:border-emerald-500/30">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg text-emerald-700 dark:text-emerald-400">Creator Commission</CardTitle>
                          <CardDescription>Earned per bet on created markets</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                            {(commissionSettings.creator_commission_rate * 100).toFixed(1)}%
                          </div>
                          <p className="text-sm text-gray-600 mt-2 dark:text-gray-400">
                            Of total bet amount
                          </p>
                        </CardContent>
                      </Card>

                      <Card className="border-2 border-blue-200 dark:border-blue-500/30">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg text-blue-700 dark:text-blue-300">Referral Level 1</CardTitle>
                          <CardDescription>Direct referral commission</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                            {(commissionSettings.referral_l1_rate * 100).toFixed(1)}%
                          </div>
                          <p className="text-sm text-gray-600 mt-2 dark:text-gray-400">
                            Of credit purchases
                          </p>
                        </CardContent>
                      </Card>

                      <Card className="border-2 border-purple-200">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg text-purple-700">Referral Level 2</CardTitle>
                          <CardDescription>Indirect referral commission</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                            {(commissionSettings.referral_l2_rate * 100).toFixed(1)}%
                          </div>
                          <p className="text-sm text-gray-600 mt-2 dark:text-gray-400">
                            Of credit purchases
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200 dark:bg-amber-500/10">
                      <h4 className="font-medium text-yellow-800 mb-2">How Commissions Work:</h4>
                      <ul className="text-sm text-yellow-700 space-y-1">
                        <li><strong>Creator Commission:</strong> Paid when users place bets on markets created by creators</li>
                        <li><strong>Referral L1:</strong> Paid when directly referred users purchase credits</li>
                        <li><strong>Referral L2:</strong> Paid when users referred by your referrals purchase credits</li>
                        <li><strong>All commissions are automatically credited to user accounts</strong></li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Fee Settings Tab */}
            <TabsContent value="fees" className="mt-6">
              <div className="space-y-6">
                {/* Fee Configuration Card */}
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Settings className="h-5 w-5" />
                          Platform Fee Settings
                        </CardTitle>
                        <CardDescription>
                          Configure trading fees, settlement spreads, and platform charges
                        </CardDescription>
                      </div>
                      <Button onClick={() => setShowFeeDialog(true)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Update Settings
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <Card className="border-2 border-blue-200 dark:border-blue-500/30">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg text-blue-700 dark:text-blue-300">Trading Fee</CardTitle>
                          <CardDescription>Charged when users place bets</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                            {(feeSettings.trading_fee_rate * 100).toFixed(1)}%
                          </div>
                          <p className="text-sm text-gray-600 mt-2 dark:text-gray-400">
                            Applied to each bet amount before pool calculation
                          </p>
                        </CardContent>
                      </Card>

                      <Card className="border-2 border-green-200">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg text-green-700">Settlement Spread</CardTitle>
                          <CardDescription>Deducted from winning payouts</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="text-3xl font-bold text-green-600">
                            {(feeSettings.settlement_spread_rate * 100).toFixed(1)}%
                          </div>
                          <p className="text-sm text-gray-600 mt-2 dark:text-gray-400">
                            Reduces payout amounts when markets are settled
                          </p>
                        </CardContent>
                      </Card>

                      <Card className="border-2 border-purple-200">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg text-purple-700">Platform Fee</CardTitle>
                          <CardDescription>Additional platform charge</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                            {(feeSettings.platform_fee_rate * 100).toFixed(1)}%
                          </div>
                          <p className="text-sm text-gray-600 mt-2 dark:text-gray-400">
                            Extra fee applied to betting transactions
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-amber-500/10">
                        <h4 className="font-semibold text-yellow-800 mb-2">Fee Limits</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Minimum Fee:</span>
                            <span className="font-medium">{feeSettings.minimum_fee_amount} credits</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Maximum Fee:</span>
                            <span className="font-medium">{feeSettings.maximum_fee_amount} credits</span>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-500/10 dark:border-blue-500/30">
                        <h4 className="font-semibold text-blue-800 mb-2">Combined Effect</h4>
                        <div className="text-sm">
                          <p>On a 100 credit bet:</p>
                          <ul className="mt-2 space-y-1 text-xs">
                            <li>• Trading fee: {(100 * feeSettings.trading_fee_rate).toFixed(1)} credits</li>
                            <li>• Platform fee: {(100 * feeSettings.platform_fee_rate).toFixed(1)} credits</li>
                            <li>• Effective bet: {(100 - (100 * (feeSettings.trading_fee_rate + feeSettings.platform_fee_rate))).toFixed(1)} credits</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Fee Analytics Card */}
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <BarChart3 className="h-5 w-5" />
                          Fee Analytics
                        </CardTitle>
                        <CardDescription>
                          Platform revenue from trading fees and spreads
                        </CardDescription>
                      </div>
                      <Button 
                        onClick={async () => {
                          setAnalyticsLoading(true);
                          try {
                            const [feeRes, winLossRes] = await Promise.all([
                              api.admin.getFeeAnalytics(30),
                              api.admin.getWinLossAnalytics(30)
                            ]);
                            setFeeAnalytics(feeRes.data);
                            setWinLossAnalytics(winLossRes.data);
                          } catch (error) {
                            toast.error('Failed to load analytics');
                          }
                          setAnalyticsLoading(false);
                        }}
                        disabled={analyticsLoading}
                      >
                        {analyticsLoading ? 'Loading...' : 'Refresh Analytics'}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {feeAnalytics ? (
                      <div className="space-y-6">
                        {/* Fee Revenue Summary */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <Card className="bg-emerald-50 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30">
                            <CardContent className="pt-4">
                              <div className="text-center">
                                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                                  {feeAnalytics.total_fees_collected?.toFixed(1) || '0.0'}
                                </div>
                                <p className="text-sm text-emerald-700 dark:text-emerald-400">Total Fees (30d)</p>
                              </div>
                            </CardContent>
                          </Card>

                          <Card className="bg-blue-50 border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/30">
                            <CardContent className="pt-4">
                              <div className="text-center">
                                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                  {feeAnalytics.trading_fees?.toFixed(1) || '0.0'}
                                </div>
                                <p className="text-sm text-blue-700 dark:text-blue-300">Trading Fees</p>
                              </div>
                            </CardContent>
                          </Card>

                          <Card className="bg-purple-50 border-purple-200 dark:bg-purple-500/10">
                            <CardContent className="pt-4">
                              <div className="text-center">
                                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                  {feeAnalytics.platform_fees?.toFixed(1) || '0.0'}
                                </div>
                                <p className="text-sm text-purple-700">Platform Fees</p>
                              </div>
                            </CardContent>
                          </Card>

                          <Card className="bg-orange-50 border-orange-200">
                            <CardContent className="pt-4">
                              <div className="text-center">
                                <div className="text-2xl font-bold text-orange-600">
                                  {feeAnalytics.settlement_fees?.toFixed(1) || '0.0'}
                                </div>
                                <p className="text-sm text-orange-700">Settlement Fees</p>
                              </div>
                            </CardContent>
                          </Card>
                        </div>

                        {/* Detailed Metrics */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="p-4 border rounded-lg">
                            <h4 className="font-semibold mb-3">Fee Performance</h4>
                            <div className="space-y-3 text-sm">
                              <div className="flex justify-between">
                                <span>Fee Transactions:</span>
                                <span className="font-medium">{feeAnalytics.fee_transactions_count || 0}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Average Fee/Transaction:</span>
                                <span className="font-medium">{feeAnalytics.average_fee_per_transaction?.toFixed(2) || '0.00'} credits</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Effective Fee Rate:</span>
                                <span className="font-medium">{feeAnalytics.effective_fee_rate?.toFixed(2) || '0.00'}%</span>
                              </div>
                            </div>
                          </div>

                          <div className="p-4 border rounded-lg">
                            <h4 className="font-semibold mb-3">Revenue Breakdown</h4>
                            <div className="space-y-2">
                              {Object.entries(feeAnalytics.fees_by_type || {}).map(([type, amount]) => (
                                <div key={type} className="flex justify-between text-sm">
                                  <span className="capitalize">{type.replace('_', ' ')}:</span>
                                  <span className="font-medium">{amount.toFixed(1)} credits</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">Click "Refresh Analytics" to load fee data</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Win/Loss Analytics Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Win/Loss Ratio Analytics
                    </CardTitle>
                    <CardDescription>
                      Platform profit analysis and house edge monitoring
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {winLossAnalytics ? (
                      <div className="space-y-6">
                        {/* Key Metrics */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <Card className="bg-gray-50 border-gray-200 dark:bg-slate-800/60 dark:border-slate-700">
                            <CardContent className="pt-4">
                              <div className="text-center">
                                <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                                  {winLossAnalytics.win_rate_percentage?.toFixed(1) || '0.0'}%
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Win Rate</p>
                              </div>
                            </CardContent>
                          </Card>

                          <Card className="bg-red-50 border-red-200 dark:bg-rose-500/10 dark:border-rose-500/30">
                            <CardContent className="pt-4">
                              <div className="text-center">
                                <div className="text-2xl font-bold text-red-600 dark:text-rose-400">
                                  {winLossAnalytics.house_edge_percentage?.toFixed(2) || '0.00'}%
                                </div>
                                <p className="text-sm text-red-700 dark:text-rose-400">House Edge</p>
                              </div>
                            </CardContent>
                          </Card>

                          <Card className="bg-green-50 border-green-200 dark:bg-emerald-500/10">
                            <CardContent className="pt-4">
                              <div className="text-center">
                                <div className="text-2xl font-bold text-green-600">
                                  {winLossAnalytics.net_platform_revenue?.toFixed(0) || '0'}
                                </div>
                                <p className="text-sm text-green-700">Net Revenue</p>
                              </div>
                            </CardContent>
                          </Card>

                          <Card className="bg-blue-50 border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/30">
                            <CardContent className="pt-4">
                              <div className="text-center">
                                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                  {winLossAnalytics.total_bets || 0}
                                </div>
                                <p className="text-sm text-blue-700 dark:text-blue-300">Total Bets (30d)</p>
                              </div>
                            </CardContent>
                          </Card>
                        </div>

                        {/* Detailed Analysis */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="p-4 border rounded-lg">
                            <h4 className="font-semibold mb-3">Betting Volume</h4>
                            <div className="space-y-3 text-sm">
                              <div className="flex justify-between">
                                <span>Total Stake:</span>
                                <span className="font-medium">{winLossAnalytics.total_stake?.toFixed(0) || '0'} credits</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Total Payouts:</span>
                                <span className="font-medium">{winLossAnalytics.total_payouts?.toFixed(0) || '0'} credits</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Average Bet Size:</span>
                                <span className="font-medium">{winLossAnalytics.average_bet_size?.toFixed(1) || '0.0'} credits</span>
                              </div>
                            </div>
                          </div>

                          <div className="p-4 border rounded-lg">
                            <h4 className="font-semibold mb-3">Revenue Sources</h4>
                            <div className="space-y-3 text-sm">
                              <div className="flex justify-between">
                                <span>From Losing Bets:</span>
                                <span className="font-medium text-green-600">+{winLossAnalytics.platform_revenue_from_losses?.toFixed(0) || '0'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>From Fees:</span>
                                <span className="font-medium text-green-600">+{winLossAnalytics.platform_revenue_from_fees?.toFixed(0) || '0'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Winning Payouts:</span>
                                <span className="font-medium text-red-600 dark:text-rose-400">-{winLossAnalytics.total_payouts?.toFixed(0) || '0'}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Health Indicators */}
                        <div className="p-4 bg-gray-50 rounded-lg dark:bg-slate-800/60">
                          <h4 className="font-semibold mb-3">Platform Health</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div className="text-center">
                              <div className={`text-lg font-bold ${winLossAnalytics.house_edge_percentage > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {winLossAnalytics.house_edge_percentage > 0 ? 'Profitable' : 'Loss Making'}
                              </div>
                              <p className="text-gray-600 dark:text-gray-400">Current Status</p>
                            </div>
                            <div className="text-center">
                              <div className={`text-lg font-bold ${winLossAnalytics.win_rate_percentage < 60 ? 'text-green-600' : 'text-yellow-600'}`}>
                                {winLossAnalytics.win_rate_percentage < 60 ? 'Healthy' : 'Monitor'}
                              </div>
                              <p className="text-gray-600 dark:text-gray-400">Win Rate Status</p>
                            </div>
                            <div className="text-center">
                              <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                                {((winLossAnalytics.platform_revenue_from_fees / winLossAnalytics.total_stake) * 100).toFixed(2)}%
                              </div>
                              <p className="text-gray-600 dark:text-gray-400">Fee Revenue Rate</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">Click "Refresh Analytics" to load win/loss data</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Transactions Tab */}
            <TabsContent value="transactions" className="mt-6">
              <TransactionsTab />
            </TabsContent>

            {/* Creators Management Tab */}
            <TabsContent value="creators" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Creator Applications
                  </CardTitle>
                  <CardDescription>
                    Review and manage creator applications
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {creatorApplications.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 dark:text-gray-400">No creator applications yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {creatorApplications.map((application) => (
                        <Card key={application.id} className="border">
                          <CardHeader>
                            <div className="flex justify-between items-start">
                              <div>
                                <CardTitle className="text-lg">
                                  {application.user?.handle || 'Unknown User'}
                                </CardTitle>
                                <CardDescription>
                                  {application.user?.email}
                                </CardDescription>
                              </div>
                              <Badge variant={
                                application.status === 'approved' ? 'default' :
                                application.status === 'pending' ? 'secondary' :
                                'destructive'
                              }>
                                {application.status}
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              <div>
                                <Label className="font-medium">Application Text:</Label>
                                <p className="text-sm text-gray-600 mt-1 dark:text-gray-400">
                                  {application.application_text}
                                </p>
                              </div>
                              
                              {application.experience && (
                                <div>
                                  <Label className="font-medium">Experience:</Label>
                                  <p className="text-sm text-gray-600 mt-1 dark:text-gray-400">
                                    {application.experience}
                                  </p>
                                </div>
                              )}
                              
                              {application.portfolio_url && (
                                <div>
                                  <Label className="font-medium">Portfolio:</Label>
                                  {safeExternalHref(application.portfolio_url) ? (
                                    <a
                                      href={safeExternalHref(application.portfolio_url)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm text-emerald-600 hover:underline mt-1 block dark:text-emerald-400"
                                    >
                                      {application.portfolio_url}
                                    </a>
                                  ) : (
                                    <span className="text-sm text-muted-foreground mt-1 block break-all">
                                      {application.portfolio_url}
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Applied: {new Date(application.created_at).toLocaleDateString()}
                              </div>
                              
                              {application.status === 'pending' && (
                                <div className="flex gap-2 pt-4 border-t">
                                  <Button 
                                    size="sm" 
                                    onClick={() => reviewCreatorApplication(application.id, 'approved')}
                                    className="flex-1"
                                  >
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Approve
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="destructive"
                                    onClick={() => reviewCreatorApplication(application.id, 'rejected')}
                                    className="flex-1"
                                  >
                                    <XCircle className="h-4 w-4 mr-2" />
                                    Reject
                                  </Button>
                                </div>
                              )}
                              
                              {application.admin_notes && (
                                <div className="pt-4 border-t">
                                  <Label className="font-medium">Admin Notes:</Label>
                                  <p className="text-sm text-gray-600 mt-1 dark:text-gray-400">
                                    {application.admin_notes}
                                  </p>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Market Approval Tab */}
            <TabsContent value="approval" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    Pending Market Approvals
                  </CardTitle>
                  <CardDescription>
                    Review and approve markets created by creators
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {pendingMarkets.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 dark:text-gray-400">No pending market approvals</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {pendingMarkets.map((market) => (
                        <Card key={market.id} className="border">
                          <CardHeader>
                            <div className="flex justify-between items-start">
                              <div>
                                <CardTitle className="text-lg">{market.title}</CardTitle>
                                <CardDescription>
                                  Created by: {market.creator?.handle || 'Unknown Creator'}
                                </CardDescription>
                              </div>
                              <Badge variant="secondary">Pending Approval</Badge>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              {/* Bild-Vorschau, damit der Admin vor der Freigabe
                                  prüfen kann, dass kein verbotenes/unpassendes
                                  Bild verwendet wird. URL mit anzeigen (Quelle). */}
                              <div>
                                <Label className="font-medium">Bild (vor Freigabe prüfen):</Label>
                                {market.image_url ? (
                                  <div className="mt-1">
                                    <img
                                      src={market.image_url}
                                      alt="Markt-Bild Vorschau"
                                      className="max-h-48 rounded-lg border border-gray-200 dark:border-slate-700 object-contain bg-gray-50 dark:bg-slate-800/60"
                                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-all">{market.image_url}</p>
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Kein Bild hinterlegt.</p>
                                )}
                              </div>

                              <div>
                                <Label className="font-medium">Description:</Label>
                                <p className="text-sm text-gray-600 mt-1 dark:text-gray-400">
                                  {market.description}
                                </p>
                              </div>
                              
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                  <Label className="font-medium">Category:</Label>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">{market.category}</p>
                                </div>
                                <div>
                                  <Label className="font-medium">Closes At:</Label>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {new Date(market.closes_at).toLocaleDateString()}
                                  </p>
                                </div>
                                <div>
                                  <Label className="font-medium">Resolution Source:</Label>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">{market.resolution_source}</p>
                                </div>
                              </div>
                              
                              {market.slug && (
                                <div>
                                  <Label className="font-medium">Slug (Future URL):</Label>
                                  <code className="block text-sm bg-gray-100 p-2 rounded mt-1 dark:bg-slate-800">
                                    predfuture.com/m/{market.slug}
                                  </code>
                                </div>
                              )}
                              
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Created: {new Date(market.created_at).toLocaleDateString()}
                              </div>
                              
                              <div className="flex gap-2 pt-4 border-t">
                                <Button 
                                  onClick={() => approveMarket(market.id, 'approved')}
                                  className="flex-1"
                                >
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Approve Market
                                </Button>
                                <Button 
                                  variant="destructive"
                                  onClick={() => {
                                    const reason = prompt('Rejection reason (optional):');
                                    approveMarket(market.id, 'rejected', reason);
                                  }}
                                  className="flex-1"
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Reject Market
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
              </div>
              {/* /content column */}
            </div>
            {/* /sidebar+content grid */}
          </Tabs>
        </div>
      </div>
      {/* User Details Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
            <DialogDescription>
              Complete user information and activity
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">User Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Handle:</span>
                      <span className="font-medium">{selectedUser?.user?.handle || selectedUser?.handle || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Email:</span>
                      <span className="font-medium">{selectedUser?.user?.email || selectedUser?.email || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Roles:</span>
                      <div className="flex gap-1">
                        {(selectedUser?.user?.roles || selectedUser?.roles || []).map(role => (
                          <Badge key={role} variant={role === 'admin' ? 'default' : 'secondary'}>
                            {role}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Registered:</span>
                      <span className="font-medium">
                        {selectedUser?.stats?.registration_date 
                          ? new Date(selectedUser.stats.registration_date).toLocaleDateString()
                          : selectedUser?.user?.created_at 
                            ? new Date(selectedUser.user.created_at).toLocaleDateString()
                            : 'Unknown'
                        }
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Last Activity:</span>
                      <span className="font-medium">
                        {selectedUser?.stats?.last_activity 
                          ? new Date(selectedUser.stats.last_activity).toLocaleDateString()
                          : 'Never'
                        }
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Statistics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Credits Balance:</span>
                      <span className="font-bold text-emerald-600 dark:text-emerald-400">
                        {selectedUser?.stats?.credits_balance?.toLocaleString() || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Total Bets:</span>
                      <span className="font-medium">{selectedUser?.stats?.total_bets || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Total Wagered:</span>
                      <span className="font-medium">{selectedUser?.stats?.total_wagered?.toLocaleString() || 0} credits</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Total Winnings:</span>
                      <span className="font-medium text-green-600">
                        {selectedUser?.stats?.total_winnings?.toLocaleString() || 0} credits
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Win Rate:</span>
                      <span className="font-medium">{selectedUser?.stats?.win_rate || 0}%</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Admin Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Admin Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditUser(selectedUser)}
                    >
                      <Settings className="h-4 w-4 mr-1" />
                      Edit User
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const amount = prompt("Enter credit adjustment amount (positive to add, negative to subtract):");
                        const reason = prompt("Enter reason for adjustment:");
                        if (amount && reason) {
                          handleAdjustCredits(selectedUser.user.id, parseFloat(amount), reason);
                        }
                      }}
                    >
                      <DollarSign className="h-4 w-4 mr-1" />
                      Adjust Credits
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleResetPassword(selectedUser?.user?.id || selectedUser?.id, selectedUser?.user?.handle || selectedUser?.handle)}
                    >
                      Reset Password
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteUser(selectedUser?.user?.id || selectedUser?.id, selectedUser?.user?.handle || selectedUser?.handle)}
                    >
                      Delete User
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="bets" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="bets">Recent Bets</TabsTrigger>
                      <TabsTrigger value="transactions">Transactions</TabsTrigger>
                      <TabsTrigger value="orders">Orders</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="bets" className="mt-4">
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedUser?.recent_activity?.bets?.map((bet, index) => (
                          <div key={index} className="p-2 bg-gray-50 rounded text-sm dark:bg-slate-800/60">
                            <div className="flex justify-between">
                              <span>Bet: {bet.stake} credits</span>
                              <Badge variant="secondary">{bet.status}</Badge>
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              {new Date(bet.placed_at).toLocaleString()}
                            </div>
                          </div>
                        )) || []}
                        {(!selectedUser?.recent_activity?.bets || selectedUser?.recent_activity?.bets?.length === 0) && (
                          <p className="text-gray-500 text-center py-4 dark:text-gray-400">No recent bets</p>
                        )}
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="transactions" className="mt-4">
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedUser?.recent_activity?.transactions?.map((tx, index) => (
                          <div key={index} className="p-2 bg-gray-50 rounded text-sm dark:bg-slate-800/60">
                            <div className="flex justify-between">
                              <span className={tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}>
                                {tx.type === 'credit' ? '+' : '-'}{tx.amount} credits
                              </span>
                              <Badge variant="secondary">{tx.reason}</Badge>
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              {new Date(tx.created_at).toLocaleString()}
                            </div>
                          </div>
                        )) || []}
                        {(!selectedUser?.recent_activity?.transactions || selectedUser?.recent_activity?.transactions?.length === 0) && (
                          <p className="text-gray-500 text-center py-4 dark:text-gray-400">No recent transactions</p>
                        )}
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="orders" className="mt-4">
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedUser?.recent_activity?.orders?.map((order, index) => (
                          <div key={index} className="p-2 bg-gray-50 rounded text-sm dark:bg-slate-800/60">
                            <div className="flex justify-between">
                              <span>{order.item_name}</span>
                              <Badge variant="secondary">{order.status}</Badge>
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              {order.total_credits} credits • {new Date(order.order_date).toLocaleDateString()}
                            </div>
                          </div>
                        )) || []}
                        {(!selectedUser?.recent_activity?.orders || selectedUser.recent_activity.orders.length === 0) && (
                          <p className="text-gray-500 text-center py-4 dark:text-gray-400">No recent orders</p>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <EditUserDialog
        isOpen={showEditUserDialog}
        onClose={() => setShowEditUserDialog(false)}
        user={selectedUser}
        onUserUpdated={fetchAdminData}
      />

      {/* Edit Market Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Market</DialogTitle>
            <DialogDescription>
              {editMarket && marketOutcomes[editMarket.id]?.activeBets > 0 ? 
                "This market has active bets. Only the end time can be modified." :
                "Update market details and settings."
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditMarketSubmit} className="space-y-4">
            <div>
              <Label htmlFor="edit_title">Title</Label>
              <Input
                id="edit_title"
                value={editMarketData.title}
                onChange={(e) => setEditMarketData({...editMarketData, title: e.target.value})}
                placeholder="Market title"
                required
                disabled={editMarket && marketOutcomes[editMarket.id]?.activeBets > 0}
              />
            </div>
            
            <div>
              <Label htmlFor="edit_description">Description</Label>
              <textarea
                id="edit_description"
                className="w-full p-2 border rounded-md"
                rows="3"
                value={editMarketData.description}
                onChange={(e) => setEditMarketData({...editMarketData, description: e.target.value})}
                placeholder="Market description"
                required
                disabled={editMarket && marketOutcomes[editMarket.id]?.activeBets > 0}
              />
            </div>
            
            <div>
              <Label htmlFor="edit_category">Category</Label>
              <select
                id="edit_category"
                className="w-full p-2 border rounded-md"
                value={editMarketData.category}
                onChange={(e) => setEditMarketData({...editMarketData, category: e.target.value})}
                required
                disabled={editMarket && marketOutcomes[editMarket.id]?.activeBets > 0}
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label htmlFor="edit_closes_at">End Date & Time</Label>
              <Input
                id="edit_closes_at"
                type="datetime-local"
                value={editMarketData.closes_at}
                onChange={(e) => setEditMarketData({...editMarketData, closes_at: e.target.value})}
                required
              />
            </div>

            <div>
              <Label htmlFor="edit_image_url">Cover-Bild (optional)</Label>
              <ImageUploadField
                value={editMarketData.image_url}
                onChange={(url) => setEditMarketData({...editMarketData, image_url: url})}
              />
              <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                Bei aktiven Wetten ist nur dieses Feld + Deadline editierbar.
              </p>
              {editMarketData.image_url && (
                <img
                  src={editMarketData.image_url}
                  alt="Preview"
                  className="mt-2 max-h-32 rounded border border-gray-200 object-cover dark:border-slate-700"
                  onError={(e) => { e.target.style.display = 'none'; }}
                  onLoad={(e) => { e.target.style.display = ''; }}
                />
              )}
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Update Market
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credit Package Dialog */}
      <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingPackage ? 'Edit Credit Package' : 'Create Credit Package'}
            </DialogTitle>
            <DialogDescription>
              {editingPackage ? 'Update package details and pricing' : 'Create a new credit package with pricing and bonuses'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSavePackage} className="space-y-4">
            <div>
              <Label htmlFor="package_name">Package Name</Label>
              <Input
                id="package_name"
                value={packageFormData.name}
                onChange={(e) => setPackageFormData({...packageFormData, name: e.target.value})}
                placeholder="e.g., Starter Pack"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="package_credits">Base Credits</Label>
                <Input
                  id="package_credits"
                  type="number"
                  min="1"
                  value={packageFormData.credits}
                  onChange={(e) => setPackageFormData({...packageFormData, credits: parseInt(e.target.value) || 0})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="package_bonus">Bonus Credits</Label>
                <Input
                  id="package_bonus"
                  type="number"
                  min="0"
                  value={packageFormData.bonus_credits}
                  onChange={(e) => setPackageFormData({...packageFormData, bonus_credits: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="package_price">Price (EUR)</Label>
              <Input
                id="package_price"
                type="number"
                min="10"
                step="0.01"
                value={packageFormData.price}
                onChange={(e) => setPackageFormData({...packageFormData, price: parseFloat(e.target.value) || 10})}
                required
              />
              <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">Minimum price: €10</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="package_order">Display Order</Label>
                <Input
                  id="package_order"
                  type="number"
                  min="0"
                  value={packageFormData.display_order}
                  onChange={(e) => setPackageFormData({...packageFormData, display_order: parseInt(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="package_popular"
                    checked={packageFormData.popular}
                    onChange={(e) => setPackageFormData({...packageFormData, popular: e.target.checked})}
                    className="rounded border-gray-300 dark:border-slate-600"
                  />
                  <Label htmlFor="package_popular">Mark as Popular</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="package_active"
                    checked={packageFormData.active}
                    onChange={(e) => setPackageFormData({...packageFormData, active: e.target.checked})}
                    className="rounded border-gray-300 dark:border-slate-600"
                  />
                  <Label htmlFor="package_active">Active</Label>
                </div>
              </div>
            </div>
            
            {/* Preview */}
            <div className="bg-gray-50 p-4 rounded-lg dark:bg-slate-800/60">
              <h4 className="font-medium mb-2">Preview:</h4>
              <div className="text-sm space-y-1">
                <div>Total Credits: <span className="font-medium">{packageFormData.credits + packageFormData.bonus_credits}</span></div>
                <div>Price per Credit: <span className="font-medium">€{packageFormData.price > 0 ? (packageFormData.price / (packageFormData.credits + packageFormData.bonus_credits)).toFixed(3) : '0.000'}</span></div>
                {packageFormData.bonus_credits > 0 && (
                  <div className="text-emerald-600 dark:text-emerald-400">Bonus: +{packageFormData.bonus_credits} credits!</div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setShowPackageDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingPackage ? 'Update Package' : 'Create Package'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Create a new user account with specific roles and permissions
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <Label htmlFor="create_user_handle">Username/Handle</Label>
              <Input
                id="create_user_handle"
                value={newUserData.handle}
                onChange={(e) => setNewUserData({...newUserData, handle: e.target.value})}
                placeholder="Enter username"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="create_user_email">Email Address</Label>
              <Input
                id="create_user_email"
                type="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                placeholder="Enter email address"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="create_user_password">Password</Label>
              <Input
                id="create_user_password"
                type="password"
                value={newUserData.password}
                onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                placeholder="Enter password (min 6 characters)"
                required
                minLength="6"
              />
              <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">Password must be at least 6 characters long</p>
            </div>
            
            <div>
              <Label>User Roles</Label>
              <div className="space-y-2 mt-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="role_user"
                    checked={newUserData.roles.includes('user')}
                    onChange={(e) => handleUserRoleChange('user', e.target.checked)}
                    className="rounded border-gray-300 dark:border-slate-600"
                  />
                  <Label htmlFor="role_user" className="text-sm">User (Default)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="role_creator"
                    checked={newUserData.roles.includes('creator')}
                    onChange={(e) => handleUserRoleChange('creator', e.target.checked)}
                    className="rounded border-gray-300 dark:border-slate-600"
                  />
                  <Label htmlFor="role_creator" className="text-sm">Creator</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="role_admin"
                    checked={newUserData.roles.includes('admin')}
                    onChange={(e) => handleUserRoleChange('admin', e.target.checked)}
                    className="rounded border-gray-300 dark:border-slate-600"
                  />
                  <Label htmlFor="role_admin" className="text-sm">Admin</Label>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">Select one or more roles for this user</p>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setShowCreateUserDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Create User
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Commission Settings Dialog */}
      <Dialog open={showCommissionDialog} onOpenChange={setShowCommissionDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Commission Settings</DialogTitle>
            <DialogDescription>
              Update commission rates for creators and referrals
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleUpdateCommissionSettings} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="creator_rate">Creator Commission Rate (%)</Label>
                <Input
                  id="creator_rate"
                  type="number"
                  min="0"
                  max="50"
                  step="0.1"
                  value={(commissionFormData.creator_commission_rate * 100).toFixed(1)}
                  onChange={(e) => setCommissionFormData({
                    ...commissionFormData, 
                    creator_commission_rate: parseFloat(e.target.value) / 100
                  })}
                  required
                />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Percentage of bet amount paid to market creators
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="referral_l1_rate">Referral Level 1 Rate (%)</Label>
                <Input
                  id="referral_l1_rate"
                  type="number"
                  min="0"
                  max="50"
                  step="0.1"
                  value={(commissionFormData.referral_l1_rate * 100).toFixed(1)}
                  onChange={(e) => setCommissionFormData({
                    ...commissionFormData, 
                    referral_l1_rate: parseFloat(e.target.value) / 100
                  })}
                  required
                />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Percentage of credit purchases for direct referrals
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="referral_l2_rate">Referral Level 2 Rate (%)</Label>
                <Input
                  id="referral_l2_rate"
                  type="number"
                  min="0"
                  max="50"
                  step="0.1"
                  value={(commissionFormData.referral_l2_rate * 100).toFixed(1)}
                  onChange={(e) => setCommissionFormData({
                    ...commissionFormData, 
                    referral_l2_rate: parseFloat(e.target.value) / 100
                  })}
                  required
                />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Percentage of credit purchases for indirect referrals
                </p>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowCommissionDialog(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                <Save className="h-4 w-4 mr-2" />
                Update Settings
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Fee Settings Dialog */}
      <Dialog open={showFeeDialog} onOpenChange={setShowFeeDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Platform Fee Settings</DialogTitle>
            <DialogDescription>
              Configure trading fees, settlement spreads, and platform charges
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleUpdateFeeSettings} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Trading Fee */}
              <div className="space-y-2">
                <Label htmlFor="trading_fee_rate">Trading Fee Rate (%)</Label>
                <Input
                  id="trading_fee_rate"
                  name="trading_fee_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="10"
                  defaultValue={(feeSettings.trading_fee_rate * 100).toFixed(2)}
                  required
                />
                <p className="text-xs text-gray-600 dark:text-gray-400">Charged when users place bets</p>
              </div>

              {/* Settlement Spread */}
              <div className="space-y-2">
                <Label htmlFor="settlement_spread_rate">Settlement Spread (%)</Label>
                <Input
                  id="settlement_spread_rate"
                  name="settlement_spread_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="10"
                  defaultValue={(feeSettings.settlement_spread_rate * 100).toFixed(2)}
                  required
                />
                <p className="text-xs text-gray-600 dark:text-gray-400">Deducted from winning payouts</p>
              </div>

              {/* Platform Fee */}
              <div className="space-y-2">
                <Label htmlFor="platform_fee_rate">Platform Fee Rate (%)</Label>
                <Input
                  id="platform_fee_rate"
                  name="platform_fee_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="5"
                  defaultValue={(feeSettings.platform_fee_rate * 100).toFixed(3)}
                  required
                />
                <p className="text-xs text-gray-600 dark:text-gray-400">Additional platform charge on bets</p>
              </div>

              {/* Minimum Fee */}
              <div className="space-y-2">
                <Label htmlFor="minimum_fee_amount">Minimum Fee (Credits)</Label>
                <Input
                  id="minimum_fee_amount"
                  name="minimum_fee_amount"
                  type="number"
                  step="0.01"
                  min="0"
                  defaultValue={feeSettings.minimum_fee_amount}
                  required
                />
                <p className="text-xs text-gray-600 dark:text-gray-400">Minimum fee amount per transaction</p>
              </div>

              {/* Maximum Fee */}
              <div className="space-y-2">
                <Label htmlFor="maximum_fee_amount">Maximum Fee (Credits)</Label>
                <Input
                  id="maximum_fee_amount"
                  name="maximum_fee_amount"
                  type="number"
                  step="1"
                  min="1"
                  defaultValue={feeSettings.maximum_fee_amount}
                  required
                />
                <p className="text-xs text-gray-600 dark:text-gray-400">Maximum fee amount per transaction</p>
              </div>
            </div>

            {/* Fee Preview */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-500/10 dark:border-blue-500/30">
              <h4 className="font-semibold text-blue-800 mb-3">Fee Impact Preview</h4>
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-blue-700 dark:text-blue-300"><strong>100 Credit Bet:</strong></p>
                    <ul className="mt-1 space-y-1 text-xs">
                      <li>• Trading fee: {(100 * feeSettings.trading_fee_rate).toFixed(1)} credits</li>
                      <li>• Platform fee: {(100 * feeSettings.platform_fee_rate).toFixed(1)} credits</li>
                      <li>• Effective bet: {(100 - (100 * (feeSettings.trading_fee_rate + feeSettings.platform_fee_rate))).toFixed(1)} credits</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-blue-700 dark:text-blue-300"><strong>200 Credit Payout:</strong></p>
                    <ul className="mt-1 space-y-1 text-xs">
                      <li>• Settlement spread: {(200 * feeSettings.settlement_spread_rate).toFixed(1)} credits</li>
                      <li>• Net payout: {(200 - (200 * feeSettings.settlement_spread_rate)).toFixed(1)} credits</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-amber-500/10">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 dark:text-yellow-400" />
                <div className="text-sm text-yellow-800">
                  <p className="font-semibold">Important:</p>
                  <p>Fee changes apply to all new bets and settlements immediately. Existing bets use their original fee structure.</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowFeeDialog(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                <Save className="h-4 w-4 mr-2" />
                Update Fee Settings
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add New Category Dialog */}
      <Dialog open={showNewCategoryDialog} onOpenChange={setShowNewCategoryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Shop Category</DialogTitle>
            <DialogDescription>
              Create a new category for organizing shop items
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateShopCategory} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="category_name">Category Name</Label>
              <Input
                id="category_name"
                placeholder="e.g., Sports & Fitness"
                value={newCategoryData.name}
                onChange={(e) => setNewCategoryData({...newCategoryData, name: e.target.value})}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category_type">Category Type</Label>
              <select
                id="category_type"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 dark:border-slate-600"
                value={newCategoryData.type}
                onChange={(e) => setNewCategoryData({...newCategoryData, type: e.target.value})}
              >
                <option value="physical">Physical Products</option>
                <option value="charity">Charity Donations</option>
              </select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowNewCategoryDialog(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>


      {/* Bet Details Dialog */}
      <Dialog open={showBetDetailsDialog} onOpenChange={setShowBetDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bet Details</DialogTitle>
            <DialogDescription>
              Complete information about this betting position
            </DialogDescription>
          </DialogHeader>
          
          {selectedBet && (
            <div className="space-y-6">
              {/* Bet Overview */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Bet Overview</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Bet ID:</Label>
                      <p className="font-mono text-sm bg-gray-100 p-2 rounded dark:bg-slate-800">{selectedBet.id}</p>
                    </div>
                    <div>
                      <Label>Status:</Label>
                      <Badge variant="default">{selectedBet.status}</Badge>
                    </div>
                    <div>
                      <Label>Stake:</Label>
                      <p className="font-bold">{selectedBet.stake.toLocaleString()} Credits</p>
                    </div>
                    <div>
                      <Label>Potential Payout:</Label>
                      <p className="font-bold text-green-600">{selectedBet.potential_payout.toLocaleString()} Credits</p>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Created At:</Label>
                    <p>{selectedBet.placed_at ? new Date(selectedBet.placed_at).toLocaleString() : '—'}</p>
                  </div>
                </CardContent>
              </Card>

              {/* User Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">User Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span>Handle:</span>
                    <span className="font-medium">{selectedBet.user?.handle}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Email:</span>
                    <span className="font-medium">{selectedBet.user?.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>User ID:</span>
                    <span className="font-mono text-sm">{selectedBet.user?.id}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Market & Position Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Position Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {selectedBet.legs.map((leg, index) => (
                    <div key={index} className="border rounded-lg p-4 bg-gray-50 dark:bg-slate-800/60">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label>Market:</Label>
                          <Badge variant="outline">{leg.market?.category}</Badge>
                        </div>
                        <p className="font-medium">{leg.market?.title}</p>
                        
                        <div className="grid grid-cols-2 gap-4 mt-3">
                          <div>
                            <Label>Position:</Label>
                            <Badge variant={leg.betting_yes ? 'default' : 'destructive'}>
                              {leg.betting_yes ? 'YES' : 'NO'}
                            </Badge>
                          </div>
                          <div>
                            <Label>Price:</Label>
                            <p className="font-bold">{leg.locked_price ? leg.locked_price.toFixed(3) : '—'}</p>
                          </div>
                        </div>
                        
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <p>Market ID: <code>{leg.market_id}</code></p>
                          <p>Outcome ID: <code>{leg.outcome_id}</code></p>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ── Bulk-Settle widget ─────────────────────────────────────────────
// A self-contained card the admin Settle tab embeds. Lists every
// closed (or still open + within deadline) market with a YES/NO
// quick picker, lets the admin select a subset and submit them all
// in one /admin/markets/bulk-settle round-trip.
const BulkSettleCard = ({ markets, marketOutcomes, onSettled }) => {
  // selections: { market_id: 'yes' | 'no' | undefined }
  const [picks, setPicks] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('closed');

  // Civic markets settle automatically at closes_at via the backend
  // background task — exclude them from the manual bulk picker.
  const candidates = markets.filter((m) => {
    if (m.mode === 'civic') return false;
    if (filter === 'closed') return m.status === 'closed';
    if (filter === 'all') return m.status === 'closed' || m.status === 'open';
    return false;
  });

  const handlePick = (marketId, side) => {
    setPicks((prev) => ({ ...prev, [marketId]: side }));
  };

  const selectedCount = Object.values(picks).filter((v) => v === 'yes' || v === 'no').length;

  const resolveOutcomeId = (market, side) => {
    const outcomes = marketOutcomes[market.id] || [];
    const wanted = (side === 'yes' ? 'YES' : 'NO').toLowerCase();
    const match = outcomes.find((o) => (o.label || '').toLowerCase() === wanted);
    return match?.id;
  };

  const handleBulkSubmit = async () => {
    const settlements = [];
    const skipped = [];
    for (const m of candidates) {
      const side = picks[m.id];
      if (side !== 'yes' && side !== 'no') continue;
      const outcomeId = resolveOutcomeId(m, side);
      if (!outcomeId) {
        skipped.push(m.title);
        continue;
      }
      settlements.push({ market_id: m.id, winning_outcome_id: outcomeId });
    }
    if (settlements.length === 0) {
      toast.error('Keine Märkte zum Settlen ausgewählt.');
      return;
    }
    if (skipped.length > 0) {
      toast.warning(`${skipped.length} Markt(e) ohne YES/NO-Outcome übersprungen`);
    }
    if (!window.confirm(`${settlements.length} Markt(e) settlen? Dies ist nicht umkehrbar.`)) return;

    setSubmitting(true);
    try {
      const res = await api.admin.bulkSettle(settlements, true);
      const data = res.data;
      const ok = data.succeeded ?? 0;
      const fail = data.failed ?? 0;
      if (fail > 0) {
        toast.warning(`${ok} settled · ${fail} failed (siehe Console)`);
        const failures = (data.results || []).filter((r) => !r.ok);
        console.warn('bulk-settle failures', failures);
      } else {
        toast.success(`${ok} Markt(e) settled`);
      }
      setPicks({});
      onSettled?.();
    } catch (err) {
      toast.error('Bulk-settle fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          Bulk Settlement
          <span className="text-xs font-normal text-gray-500 ml-2 dark:text-gray-400">
            {selectedCount} ausgewählt · {candidates.length} verfügbar
          </span>
        </CardTitle>
        <CardDescription>
          Mehrere prediction markets in einem Schritt settlen. Civic markets settlen automatisch bei closes_at.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3 mb-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-md dark:border-slate-700"
          >
            <option value="closed">Nur closed</option>
            <option value="all">Closed + open (auto-close beim Settle)</option>
          </select>
          <Button
            onClick={handleBulkSubmit}
            disabled={selectedCount === 0 || submitting}
            className="ml-auto"
          >
            {submitting ? 'Settle…' : `Settle ${selectedCount} Markt(e)`}
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 dark:bg-slate-800/60">
                <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">Market</th>
                <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">Status</th>
                <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">Closes</th>
                <th className="text-left p-2 font-medium text-gray-600 dark:text-gray-400">Winner</th>
              </tr>
            </thead>
            <tbody>
              {candidates.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-gray-500 dark:text-gray-400">
                    Keine passenden Märkte.
                  </td>
                </tr>
              ) : candidates.map((m) => {
                const side = picks[m.id];
                return (
                  <tr key={m.id} className="border-b hover:bg-gray-50 dark:hover:bg-slate-800/60">
                    <td className="p-2 truncate max-w-xs">{m.title}</td>
                    <td className="p-2">
                      <Badge variant={m.status === 'closed' ? 'default' : 'outline'} className="text-[10px]">
                        {m.status}
                      </Badge>
                    </td>
                    <td className="p-2 text-xs text-gray-500 dark:text-gray-400">
                      {m.closes_at ? new Date(m.closes_at).toLocaleString('de-DE') : '—'}
                    </td>
                    <td className="p-2">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => handlePick(m.id, side === 'yes' ? undefined : 'yes')}
                          className={`px-2.5 py-1 rounded text-xs font-semibold border ${
                            side === 'yes'
                              ? 'bg-emerald-600 text-white border-emerald-600'
                              : 'bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50'
                          }`}
                        >
                          YES
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePick(m.id, side === 'no' ? undefined : 'no')}
                          className={`px-2.5 py-1 rounded text-xs font-semibold border ${
                            side === 'no'
                              ? 'bg-rose-600 text-white border-rose-600'
                              : 'bg-white text-rose-700 border-rose-200 hover:bg-rose-50'
                          }`}
                        >
                          NO
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminPage;
